import axios from 'axios'

const normalize = (url?: string) => url ? url.replace(/\/$/, '') : undefined
const GATEWAY = normalize(import.meta.env.VITE_API_GATEWAY_URL as string | undefined)
const POSTING_URL = normalize(import.meta.env.VITE_API_POSTING_URL as string | undefined)
const JOB_URL = normalize(import.meta.env.VITE_API_JOB_URL as string | undefined)
const AUTH_URL = normalize(import.meta.env.VITE_API_AUTH_URL as string | undefined)

const API_BASE = GATEWAY ?? '/api'
const POSTING_BASE = POSTING_URL ?? API_BASE
const JOB_BASE = JOB_URL ?? API_BASE
const AUTH_BASE = AUTH_URL ?? '/auth'

export const publicPostingClient = axios.create({ baseURL: POSTING_BASE, headers: { 'Content-Type': 'application/json' } })
export const postingClient = axios.create({ baseURL: POSTING_BASE, headers: { 'Content-Type': 'application/json' } })
export const authClient    = axios.create({ baseURL: AUTH_BASE,    headers: { 'Content-Type': 'application/json' } })
export const jobClient     = axios.create({ baseURL: JOB_BASE,     headers: { 'Content-Type': 'application/json' } })

// Inject auth token from localStorage into every request
;[postingClient, authClient, jobClient].forEach(client => {
  client.interceptors.request.use(config => {
    const token = localStorage.getItem('aux_token')
    if (token) config.headers.Authorization = `Bearer ${token}`

    const rawSupport = localStorage.getItem('dd_support_session')
    if (rawSupport) {
      try {
        const parsed = JSON.parse(rawSupport) as { sessionId?: string }
        if (parsed?.sessionId) {
          config.headers['x-support-session-id'] = parsed.sessionId
        }
      } catch {
        // ignore malformed local support context
      }
    }
    return config
  })
})

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  accountType: string
  roles?: string[]
  orgId: string | null
  orgName: string | null
  orgRole: string | null
}

export interface SandboxDemoPersona {
  key: string
  accountType: string
  name: string
  email: string
  orgId: string
  orgName: string
  roles: string[]
}

export interface SandboxDemoConfigResponse {
  enabled: boolean
  personas: SandboxDemoPersona[]
}

export interface SupportSessionRecord {
  id: string
  platform_user_id: string
  target_tenant_org_id: string
  reason: string
  read_only: boolean
  status: 'ACTIVE' | 'ENDED' | 'EXPIRED'
  started_at: string
  ended_at: string | null
  expires_at: string | null
}

export interface TenantSearchResult {
  tenantId: string
  tenantName: string
  tenantType: string | null
  tenantStatus: string | null
  matchedSources: Array<'company' | 'user' | 'vin' | 'order' | 'load' | 'trip'>
  matchReasons: string[]
  technicalTenantId: string
}

export interface PlatformTenantSearchResponse {
  q: string
  results: TenantSearchResult[]
}

export interface Load {
  id: string
  loadNumber?: string
  origin: string
  destination: string
  status: 'PENDING' | 'POSTED' | 'CLAIMED' | 'ASSIGNED' | 'DRIVER_ASSIGNED' | 'PICKED_UP' | 'IN_PROGRESS' | 'IN_TRANSIT' | 'DELIVERED' | 'WAITING_POD' | 'POD_UPLOADED' | 'INVOICED' | 'PAID' | 'CANCELLED'
  customerPayCents?: number
  carrierPayCents?: number
  grossMarginCents?: number
  marginPercent?: number
  estDistanceMiles?: number
  customerRatePerMileCents?: number
  carrierRatePerMileCents?: number
  marginPerMileCents?: number
  totalPayment?: number
  // Legacy alias, maps to customer pay where available.
  totalPay?: number
  pickupDate?: string
  deliveryDate?: string
  pickupWindowStart?: string | null
  pickupWindowEnd?: string | null
  deliveryWindowStart?: string | null
  deliveryWindowEnd?: string | null
  notes?: string | null
  metadata?: Record<string, unknown>
  createdAt: string
  vehicles?: Vehicle[]
  carrierId?: string | null
  brokerId?: string | null
  vinCount?: number
  assignedCarrierName?: string | null
  assignedDriverName?: string | null
  assignedDriverUserId?: string | null
}

export interface LoadTrailerPlanVehicle {
  vehicleId: string
  vin: string | null
  year?: number | null
  make?: string | null
  model?: string | null
  color?: string | null
  bodyStyle?: string | null
  lengthIn?: number | null
  heightIn?: number | null
  weightLb?: number | null
  operable?: boolean | null
  trailerPosition?: string | null
  pickupSequence?: number | null
  deliverySequence?: number | null
  operationalState?: string | null
  notes?: string | null
  leverHeight?: number | null
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface TrailerLayoutPosition {
  id: string
  label: string
  deck: 'upper' | 'lower' | 'rear' | 'unknown'
  sequenceHint: number
  maxLengthInches?: number | null
  maxHeightInches?: number | null
  maxWeightLbs?: number | null
  allowInoperable?: boolean
}

export interface TrailerLayoutDescriptor {
  layoutId: string
  layoutName: string
  equipmentType: string
  capacity: number
  positions: TrailerLayoutPosition[]
  levels: number[]
  leverControls: Array<{ positionId: string; min: number; max: number }>
  levelScale?: { min: number; max: number } | null
  leverHeight?: { min: number; max: number } | null
  tandemMetadata?: {
    supportsSlidingTandems: boolean
    axleLabels: string[]
  } | null
}

export interface LoadTrailerPlanResponse {
  loadId: string
  loadNumber?: string | null
  status?: string | null
  route: {
    origin?: string | null
    destination?: string | null
    pickupDate?: string | null
    deliveryDate?: string | null
  }
  planner: {
    status?: string | null
    notes?: string | null
    equipmentLayoutType?: string | null
    trailerDiagramMode?: string | null
    leverHeights?: Record<string, number>
    deckStates?: Record<string, { height: number; angle: number }>
    rampStates?: Record<string, 'stored' | 'deployed' | 'raised' | 'lowered'>
    warnings?: string[]
    slotProfiles?: Record<string, {
      maxLengthInches?: number
      maxHeightInches?: number
      maxWeightLbs?: number
      allowInoperable?: boolean
    }>
    tripLayouts?: Array<{
      id: string
      name: string
      createdAt: string
      createdBy?: string | null
      vehicles: Array<{
        vehicleId: string
        trailerPosition: string | null
        pickupSequence: number | null
        deliverySequence: number | null
        notes: string | null
        operable: boolean | null
      }>
      leverHeights: Record<string, number>
      slotProfiles: Record<string, {
        maxLengthInches?: number
        maxHeightInches?: number
        maxWeightLbs?: number
        allowInoperable?: boolean
      }>
    }>
    activeTripLayoutId?: string | null
    updatedAt?: string | null
    updatedBy?: string | null
  }
  assignedEquipment?: {
    driverUserId?: string | null
    driverName?: string | null
    carrierOrgId?: string | null
    carrierName?: string | null
    powerUnitModel?: string | null
    trailerModel?: string | null
    trailerNumber?: string | null
    equipmentType?: string | null
    trailerCapacity?: number | null
    layoutType?: string | null
    geometry?: any
  }
  layoutDescriptor: TrailerLayoutDescriptor
  vehicles: LoadTrailerPlanVehicle[]
  warnings: string[]
  autoSequence?: {
    proposed: Array<{
      vehicleId: string
      trailerPosition: string | null
      pickupSequence: number | null
      deliverySequence: number | null
    }>
    warnings: string[]
  }
  driverNotes?: {
    loadOrder: string
    unloadOrder: string
  }
}

export interface LoadAssignmentHistoryEvent {
  id: string
  load_id: string
  event_type: string
  from_status?: string | null
  to_status?: string | null
  carrier_org_id?: string | null
  carrier_id?: string | null
  driver_user_id?: string | null
  actor_user_id?: string | null
  notes?: string | null
  metadata?: Record<string, unknown>
  created_at: string
}

export interface LoadTrailerRecommendationResponse {
  loadId: string
  deterministic: boolean
  status: string
  score: number
  warnings: string[]
  applied: boolean
  leverHeights: Record<string, number>
  recommendations: Array<{
    vehicleId: string
    vin?: string | null
    recommendedPosition: string | null
    pickupSequence: number | null
    deliverySequence: number | null
    confidence: number
    reason: string
  }>
}

export interface LoadAssignmentOptionsResponse {
  load: {
    id: string
    loadNumber?: string
    status?: string
    routeSummary: {
      origin?: string | null
      destination?: string | null
      pickupDate?: string | null
      deliveryDate?: string | null
    }
    trailerPlanStatus?: string | null
    vehicleCount: number
    currentAssignment: {
      carrierOrgId?: string | null
      assignedCarrierId?: string | null
      driverUserId?: string | null
      jobId?: string | null
      jobStatus?: string | null
      truckNumber?: string | null
      trailerNumber?: string | null
    }
    internalPricingSummary?: {
      customerPayCents?: number | null
      carrierPayCents?: number | null
      grossMarginCents?: number | null
    } | null
  }
  carriers: Array<{
    id: string
    aux_org_id?: string | null
    legal_name?: string | null
    dba_name?: string | null
    compliance_status?: string | null
    approval_status?: string | null
    status?: string | null
  }>
  drivers: Array<{
    id: string
    aux_user_id?: string | null
    carrier_org_id?: string | null
    legal_name?: string | null
    first_name?: string | null
    last_name?: string | null
    driver_status?: string | null
    aux_email?: string | null
  }>
  equipment: Array<{
    id: string
    aux_user_id?: string | null
    carrier_org_id?: string | null
    combo_key?: string | null
    unit_label?: string | null
    is_active?: boolean
    updated_at?: string
  }>
  complianceWarnings: string[]
  driverEquipmentWarnings: string[]
}

export interface Quote {
  id: string
  quoteNumber: string
  status: 'NEW' | 'PRICING' | 'QUOTED' | 'SENT_TO_CUSTOMER' | 'ACCEPTED' | 'CONVERTED_TO_DRAFT_LOAD' | 'CONVERTED_TO_ORDER' | 'EXPIRED' | 'REJECTED'
  customerName?: string | null
  customerPhone?: string | null
  customerEmail?: string | null
  customerReference?: string | null
  internalReference?: string | null
  originZip?: string | null
  originCity?: string | null
  originState?: string | null
  destinationZip?: string | null
  destinationCity?: string | null
  destinationState?: string | null
  vin?: string | null
  vinStatus?: string | null
  vehicleYear?: number | null
  vehicleMake?: string | null
  vehicleModel?: string | null
  vehicleType?: string | null
  vehicleTrim?: string | null
  vehicleEngine?: string | null
  vehicleFuelType?: string | null
  operable?: boolean
  transportType?: string | null
  requestedPickupDate?: string | null
  requestedDeliveryDate?: string | null
  estDistanceMiles?: number | null
  mileageProvider?: string | null
  mileageSource?: string | null
  customerQuoteCents?: number | null
  targetCarrierPayCents?: number | null
  grossMarginCents?: number | null
  marginPercent?: number | null
  customerRatePerMileCents?: number | null
  carrierRatePerMileCents?: number | null
  marginPerMileCents?: number | null
  internalNotes?: string | null
  pricingNotes?: string | null
  publicNotes?: string | null
  convertedLoadId?: string | null
  convertedOrderId?: string | null
  accountHierarchyId?: string | null
  locationHierarchyId?: string | null
  contactHierarchyId?: string | null
  billingProfileId?: string | null
  metadata?: Record<string, unknown> | null
  createdAt?: string
  updatedAt?: string
}

export interface QuotePortConfig {
  id: string
  accountSlug: string
  companyName: string
  logoUrl?: string | null
  primaryColor?: string | null
  quotePageHeadline?: string | null
  termsText?: string | null
  phone?: string | null
  isActive: boolean
  metadata?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
}

export interface QuoteRequest {
  id: string
  accountId: string
  accountSlug: string
  accountCompanyName?: string | null
  accountLogoUrl?: string | null
  assignmentState?: 'unassigned_review' | 'suggested' | 'assigned' | 'rejected' | string
  accountHierarchyId?: string | null
  locationHierarchyId?: string | null
  contactHierarchyId?: string | null
  billingProfileId?: string | null
  status:
    | 'new'
    | 'assigned'
    | 'in_progress'
    | 'waiting_on_customer'
    | 'quote_sent'
    | 'accepted'
    | 'declined'
    | 'expired'
    | 'duplicate'
    | 'archived'
    | 'converted'
    | 'rejected'
  customerName: string
  customerEmail: string
  customerPhone: string
  pickupAddress1?: string | null
  pickupAddress2?: string | null
  pickupCity: string
  pickupState: string
  pickupZip: string
  deliveryAddress1?: string | null
  deliveryAddress2?: string | null
  deliveryCity: string
  deliveryState: string
  deliveryZip: string
  requestedPickupDate?: string | null
  requestedDeliveryDate?: string | null
  vehicleYear?: number | null
  vehicleMake?: string | null
  vehicleModel?: string | null
  vin?: string | null
  vinStatus: 'missing' | 'provided' | 'needs_verification'
  runningStatus: 'running' | 'not_running' | 'unknown'
  transportType: 'OPEN' | 'ENCLOSED'
  notes?: string | null
  source: string
  consentAccepted: boolean
  metadata?: Record<string, unknown>
  quotedCustomerPriceCents?: number | null
  quotedCurrency?: string | null
  quoteMessage?: string | null
  quoteInternalNotes?: string | null
  quoteExpiresAt?: string | null
  rejectionReason?: string | null
  quotedAt?: string | null
  rejectedAt?: string | null
  customerAcceptedAt?: string | null
  customerDeclinedAt?: string | null
  convertedAt?: string | null
  convertedOrderId?: string | null
  paymentRequiredBeforePickup?: boolean
  paymentRequirementReason?: string | null
  accountTermsSnapshot?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
}

export interface QuoteResponseView {
  id: string
  status: QuoteRequest['status']
  customerName: string
  customerEmail: string
  customerPhone: string
  pickupCity: string
  pickupState: string
  pickupZip: string
  deliveryCity: string
  deliveryState: string
  deliveryZip: string
  vehicleYear?: number | null
  vehicleMake?: string | null
  vehicleModel?: string | null
  vinStatus: 'missing' | 'provided' | 'needs_verification'
  quotedCustomerPriceCents?: number | null
  quotedCurrency?: string | null
  quoteMessage?: string | null
  quoteExpiresAt?: string | null
  paymentRequiredBeforePickup?: boolean
  paymentRequirementReason?: string | null
  accountCompanyName?: string | null
  accountSlug: string
  createdAt?: string
  quotedAt?: string | null
  customerAcceptedAt?: string | null
  customerDeclinedAt?: string | null
  convertedOrderId?: string | null
}

export interface CustomerPortalDocument {
  id: string
  docType: string
  entityType: string
  entityId: string
  label: string
  status: string
  createdAt?: string
  metadata?: Record<string, unknown>
}

export interface CustomerPortalOrder {
  id: string
  orderNumber: string
  status: string
  customerName?: string | null
  customerPriceCents?: number | null
  paymentTerms?: string | null
  customerVehicleDetails?: Record<string, unknown>
  pickupSnapshot?: any
  deliverySnapshot?: any
  pickupContactName?: string | null
  pickupContactPhone?: string | null
  deliveryContactName?: string | null
  deliveryContactPhone?: string | null
  pickupAccessNotes?: string | null
  deliveryAccessNotes?: string | null
  stockNumber?: string | null
  readyDate?: string | null
  pickupWindowStart?: string | null
  pickupWindowEnd?: string | null
  deliveryWindowStart?: string | null
  deliveryWindowEnd?: string | null
  referenceNumber?: string | null
  customerCompletionState?: string | null
  customerSubmissionState?: string | null
  paymentState?: string | null
  customerSubmittedAt?: string | null
  customerAgreementSignedAt?: string | null
  customerChangeRequestState?: string | null
  customerChangeRequestReason?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface CustomerPortalResponse {
  portal: {
    token: string
    accountSlug: string
    accountCompanyName?: string | null
    accountLogoUrl?: string | null
    quoteStatus: string
    paymentState: string
    customerCompletionState: string
    customerSubmissionState: string
    customerChangeRequestState: string
  }
  quoteResponse: QuoteResponseView
  order: CustomerPortalOrder | null
  unitMoves: UnitMove[]
  documents: CustomerPortalDocument[]
  paymentState: string
}

export interface QuoteRequestEmailDelivery {
  mode: 'real_provider' | 'stub' | 'provider_not_configured' | string
  status: 'queued' | 'sent' | 'failed' | string
  deliveryStatus?: 'queued' | 'sent' | 'failed' | 'provider_not_configured' | string
  outboxId: string
  provider?: string
  error?: string | null
}

export interface Order {
  id: string
  orderNumber: string
  status: string
  sourceQuoteId?: string | null
  customerId?: string | null
  accountHierarchyId?: string | null
  locationHierarchyId?: string | null
  contactHierarchyId?: string | null
  billingProfileId?: string | null
  customerName?: string | null
  customerAccount?: string | null
  pickupPlaceId?: string | null
  deliveryPlaceId?: string | null
  pickupSnapshot?: any
  deliverySnapshot?: any
  customerPriceCents?: number | null
  carrierCostCents?: number | null
  marginCents?: number | null
  paymentTerms?: string | null
  customerVehicleDetails?: Record<string, unknown>
  readyDate?: string | null
  pickupWindowStart?: string | null
  pickupWindowEnd?: string | null
  deliveryWindowStart?: string | null
  deliveryWindowEnd?: string | null
  referenceNumber?: string | null
  notes?: string | null
  pickupContactName?: string | null
  pickupContactPhone?: string | null
  deliveryContactName?: string | null
  deliveryContactPhone?: string | null
  pickupAccessNotes?: string | null
  deliveryAccessNotes?: string | null
  stockNumber?: string | null
  customerCompletionState?: string | null
  customerSubmissionState?: string | null
  paymentState?: string | null
  customerSubmittedAt?: string | null
  customerAgreementSignedAt?: string | null
  customerChangeRequestState?: string | null
  customerChangeRequestReason?: string | null
  orgContextId?: string | null
  postedLoadId?: string | null
  assignedJobId?: string | null
  activeDispatchTripId?: string | null
  vinCount?: number
  firstVin?: string | null
  firstVehicleSummary?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface DispatchTrip {
  id: string
  tripNumber: string
  status: string
  orderId?: string | null
  loadId?: string | null
  jobId?: string | null
  carrierProfileId?: string | null
  carrierOrgId?: string | null
  driverUserId?: string | null
  trailerNumber?: string | null
  trailerId?: string | null
  trailerLayoutRef?: string | null
  dispatchAt?: string | null
  driverAckStatus?: string | null
  driverAckAt?: string | null
  assignmentNotes?: string | null
  blockers?: string[]
  createdAt?: string
  updatedAt?: string
}

export interface DispatchTripStop {
  id: string
  tripId: string
  orderId?: string | null
  stopType: string
  placeSnapshot?: any
  dateWindowStart?: string | null
  dateWindowEnd?: string | null
  appointmentRequired?: boolean
  appointmentStatus?: string
  appointmentConfirmed?: boolean
  confirmationNumber?: string | null
  accessInstructions?: string | null
  lotNotes?: string | null
  contactName?: string | null
  contactPhone?: string | null
  afterHoursAllowed?: boolean
  rescheduleState?: string | null
  stopStatus?: string
  arrivedAt?: string | null
  completedAt?: string | null
  sortOrder?: number
  createdAt?: string
  updatedAt?: string
}

export interface DispatchTripEvent {
  id: string
  tripId: string
  orderId?: string | null
  eventType: string
  actorUserId?: string | null
  actorRole?: string | null
  visibility?: string
  metadata?: Record<string, unknown>
  createdAt: string
}

export interface DispatchTripBundle {
  orderId?: string
  trip: DispatchTrip | null
  stops: DispatchTripStop[]
  units: Array<{
    id: string
    trip_id: string
    unit_move_id?: string | null
    vehicle_id?: string | null
    vin?: string | null
    trailer_slot?: string | null
    pickup_sequence?: number | null
    delivery_sequence?: number | null
    assignment_status?: string
  }>
  timeline: DispatchTripEvent[]
  trailerPlan?: any
}

export interface UnifiedTimelineEntry {
  id: string
  eventType: string
  label: string
  timestamp: string
  actorName?: string | null
  actorRole?: string | null
  relatedObjectType?: 'quote_request' | 'order' | 'trip' | 'stop' | 'unit' | 'issue' | 'system'
  relatedObjectId?: string | null
  relatedObjectLink?: string | null
  metadataSummary?: string
  metadata?: Record<string, unknown>
  visibility?: 'internal' | 'driver' | 'customer'
}

export interface GlobalSearchResult {
  q: string
  vehicles: Array<{
    id: string
    vin: string
    vinLast6: string
    vinLast8: string
    year?: number | null
    make?: string | null
    model?: string | null
    type?: string | null
    latestOrderStatus?: string | null
    latestLoadStatus?: string | null
    currentStatus?: string | null
    customerName?: string | null
  }>
  orders: Order[]
  quotes: Quote[]
  loads: Load[]
  invoices: Array<{
    id: string
    invoice_number: string
    status: string
    customer_name?: string | null
    amount?: number
    balance?: number
    gross_amount?: number
    net_amount?: number
    currency?: string
    due_date?: string | null
    paid_at?: string | null
    load_id?: string | null
    load_number?: string | null
    job_id?: string | null
    created_at?: string
  }>
  customers: Array<{
    id: string
    account_number: string
    name: string
    email?: string | null
    phone?: string | null
    payment_terms?: string | null
    status?: string
    created_at?: string
  }>
  places: Array<{
    id: string
    place_type?: string | null
    name?: string | null
    address1?: string | null
    city?: string | null
    state?: string | null
    zip?: string | null
    country?: string | null
    created_at?: string
  }>
  lanes: Lane[]
  vinInventory: VinInventoryItem[]
  carriers: Array<{
    id: string
    legal_name?: string | null
    dba_name?: string | null
    email?: string | null
    phone?: string | null
    city?: string | null
    state?: string | null
    approval_status?: string | null
    created_at?: string
  }>
  drivers: Array<{
    id: string
    aux_user_id?: string | null
    carrier_org_id?: string | null
    first_name?: string | null
    last_name?: string | null
    legal_name?: string | null
    email?: string | null
    phone?: string | null
    driver_status?: string | null
    unit_label?: string | null
    city?: string | null
    state?: string | null
    created_at?: string
  }>
}

export interface UnitMove {
  id: string
  orderId: string
  lineNumber: number
  vehicleId?: string | null
  vin: string
  customerId?: string | null
  pickupPlaceSnapshot?: any
  deliveryPlaceSnapshot?: any
  readyDate?: string | null
  status: string
  priority: string
  lane?: string | null
  notes?: string | null
  createdAt?: string
  updatedAt?: string
  vehicle?: {
    id: string
    vin: string
    year?: number | null
    make?: string | null
    model?: string | null
    color?: string | null
    bodyStyle?: string | null
  } | null
}

export interface VehicleInspectionSummary {
  id: string
  order_id: string
  unit_id: string
  load_id?: string | null
  trip_id?: string | null
  inspection_type: 'PICKUP' | 'DELIVERY'
  status: string
  started_at?: string | null
  submitted_at?: string | null
  signed_at?: string | null
  locked_at?: string | null
  voided_at?: string | null
  created_at: string
  order_number?: string | null
  photo_count?: number
  signature_count?: number
  marker_count?: number
  exception_count?: number
}

export interface DriverInspectionEntryResponse {
  inspectionId: string
  inspectionType: 'PICKUP' | 'DELIVERY'
  status: string
  publicLink: string
  publicTokenExpiresAt: string
  reused: boolean
  unitId: string
}

export interface VehicleInspectionPublicSession {
  inspection: {
    id: string
    inspectionType: 'PICKUP' | 'DELIVERY'
    status: string
    orderId: string
    unitId: string
    loadId?: string | null
    tripId?: string | null
    contactName?: string | null
    locationName?: string | null
    locationAddress?: string | null
    notes?: string | null
    inspectorName?: string | null
    inspectorRole?: string | null
    startedAt?: string | null
    submittedAt?: string | null
    signedAt?: string | null
    lockedAt?: string | null
    unit: {
      vinMasked?: string | null
      vinLast6?: string | null
      year?: number | null
      make?: string | null
      model?: string | null
    }
    orderSummary: {
      orderNumber?: string | null
      loadNumber?: string | null
    }
    comparison?: string | null
  }
  tokenExpiresAt: string
}

export interface ConditionIssue {
  id: string
  orderId: string
  unitId: string
  inspectionId: string
  inspectionType?: 'PICKUP' | 'DELIVERY'
  status: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN'
  reporterRole?: string | null
  reporterName?: string | null
  reportedArea: string
  description: string
  markerRefs?: any[]
  exceptionRefs?: any[]
  evidenceRefs?: any[]
  publicCustomerNoticeApprovedAt?: string | null
  internalNotes?: string | null
  carrierNotes?: string | null
  assignedCarrierOrgId?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface DispatchBoardCandidate {
  id: string
  sourceType: 'UNIT_MOVE' | 'VIN_INVENTORY'
  unitMoveId?: string | null
  vinInventoryItemId?: string | null
  orderId?: string | null
  orderNumber?: string | null
  customerId?: string | null
  customerName?: string | null
  laneId?: string | null
  laneNumber?: string | null
  vin: string
  vinLast8?: string | null
  vinLast6?: string | null
  year?: number | null
  make?: string | null
  model?: string | null
  status?: string | null
  decodeStatus?: string | null
  decodeError?: string | null
  originState?: string | null
  destinationState?: string | null
  pickupZip?: string | null
  deliveryZip?: string | null
  pickupCity?: string | null
  deliveryCity?: string | null
  readyDate?: string | null
  loadId?: string | null
  currentSource?: string | null
  warnings?: string[]
}

export interface DispatchBoardSummary {
  candidates: DispatchBoardCandidate[]
  totals: {
    totalCandidates: number
    unitMoveCandidates: number
    vinInventoryCandidates: number
  }
  recentLoads: Array<Load & { vehicleCount?: number; assignedCarrierName?: string | null; assignedDriverName?: string | null }>
  lanes: Lane[]
  customers: Customer[]
  carriers: Array<any>
  drivers: Array<any>
}

export interface DispatchBoardPreview {
  vehicleCount: number
  route: {
    pickupZip?: string | null
    deliveryZip?: string | null
    originState?: string | null
    destinationState?: string | null
  }
  customerId?: string | null
  laneId?: string | null
  estDistanceMiles?: number | null
  suggestedCarrierPayCents?: number | null
  suggestedCustomerPriceCents?: number | null
  suggestedMarginCents?: number | null
  warnings: string[]
}

export interface DispatchBoardCreateLoadResult {
  load: Load
  vehicleCount: number
  affectedOrderIds: string[]
  affectedUnitMoveIds: string[]
  affectedInventoryItemIds: string[]
  createdJob?: { id: string; jobNumber: string } | null
  warnings: string[]
}

export interface OrderStatusEvent {
  id: string
  order_id: string
  status: string
  note?: string | null
  changed_by?: string | null
  created_at: string
}

export interface Customer {
  id: string
  accountNumber: string
  name: string
  company?: string | null
  customerType?: string | null
  primaryContactName?: string | null
  primaryContactPhone?: string | null
  primaryContactEmail?: string | null
  billingContactName?: string | null
  billingContactPhone?: string | null
  billingContactEmail?: string | null
  phone?: string | null
  email?: string | null
  paymentTerms?: string | null
  creditLimitCents?: number | null
  status?: string
  internalNotes?: string | null
  hierarchyAccountId?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface Place {
  id: string
  placeType?: string | null
  name?: string | null
  address1?: string | null
  address2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  country?: string | null
  mainContactName?: string | null
  pickupContactName?: string | null
  deliveryContactName?: string | null
  phone?: string | null
  email?: string | null
  gateHours?: string | null
  appointmentRequired?: boolean
  buyerReleaseInfo?: string | null
  pickupInstructions?: string | null
  deliveryInstructions?: string | null
  forkliftAvailable?: boolean | null
  inopRules?: string | null
  storageFeeWarnings?: string | null
  notes?: string | null
  status?: string
  hierarchyLocationId?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface AccountHierarchyAccount {
  id: string
  customerId?: string | null
  accountCode?: string | null
  legalName: string
  displayName?: string | null
  status?: string
  creditHold?: boolean
  creditHoldReason?: string | null
  paymentTerms?: string | null
  metadata?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
}

export interface AccountHierarchyLocation {
  id: string
  accountId: string
  placeId?: string | null
  locationCode?: string | null
  name: string
  status?: string
  city?: string | null
  state?: string | null
  zip?: string | null
  metadata?: Record<string, unknown>
}

export interface AccountHierarchyContact {
  id: string
  accountId: string
  locationId?: string | null
  fullName: string
  email?: string | null
  phone?: string | null
  roleTitle?: string | null
  isPrimary?: boolean
  status?: string
  metadata?: Record<string, unknown>
}

export interface AccountHierarchyBillingProfile {
  id: string
  accountId: string
  profileName: string
  status?: string
  paymentTerms?: string | null
  creditHold?: boolean
  creditHoldReason?: string | null
  billingContactName?: string | null
  billingContactEmail?: string | null
  billingContactPhone?: string | null
}

export interface QuoteRequestAccountSuggestion {
  accountId: string
  legalName: string
  displayName?: string | null
  creditHold?: boolean
  paymentTerms?: string | null
  locationId?: string | null
  locationName?: string | null
  locationCity?: string | null
  locationState?: string | null
  contactId?: string | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  billingProfileId?: string | null
  billingProfileName?: string | null
  matchScore: number
}

export interface Lane {
  id: string
  laneNumber: string
  orgContextId?: string | null
  customerId?: string | null
  customerName?: string | null
  customerAccountNumber?: string | null
  originPlaceId?: string | null
  destinationPlaceId?: string | null
  originName?: string | null
  originCity?: string | null
  originState?: string | null
  originZip?: string | null
  destinationName?: string | null
  destinationCity?: string | null
  destinationState?: string | null
  destinationZip?: string | null
  estDistanceMiles?: number | null
  transportType?: string | null
  equipmentType?: string | null
  isOperableRequired?: boolean | null
  targetCustomerPayCents?: number | null
  targetCarrierPayCents?: number | null
  targetMarginCents?: number | null
  customerRatePerMileCents?: number | null
  carrierRatePerMileCents?: number | null
  marginPerMileCents?: number | null
  notes?: string | null
  status?: string
  archivedAt?: string | null
  metadata?: Record<string, any>
  createdBy?: string | null
  updatedBy?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface LanePriceHistoryItem {
  id: string
  lane_id: string
  previous_customer_pay_cents?: number | null
  previous_carrier_pay_cents?: number | null
  previous_margin_cents?: number | null
  previous_customer_rate_per_mile_cents?: number | null
  previous_carrier_rate_per_mile_cents?: number | null
  previous_margin_per_mile_cents?: number | null
  next_customer_pay_cents?: number | null
  next_carrier_pay_cents?: number | null
  next_margin_cents?: number | null
  next_customer_rate_per_mile_cents?: number | null
  next_carrier_rate_per_mile_cents?: number | null
  next_margin_per_mile_cents?: number | null
  changed_by?: string | null
  change_note?: string | null
  created_at: string
}

export interface VinBatch {
  id: string
  batchNumber: string
  orgContextId?: string | null
  customerId?: string | null
  customerName?: string | null
  laneId?: string | null
  laneNumber?: string | null
  sourceType?: string | null
  sourceName?: string | null
  rawCount: number
  validCount: number
  duplicateCount: number
  invalidCount: number
  itemCount?: number
  status: string
  notes?: string | null
  metadata?: Record<string, any>
  createdBy?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface VinInventoryItem {
  id: string
  orgContextId?: string | null
  batchId?: string | null
  batchNumber?: string | null
  customerId?: string | null
  customerName?: string | null
  laneId?: string | null
  laneNumber?: string | null
  vehicleId?: string | null
  vin: string
  vinLast8?: string | null
  vinLast6?: string | null
  decodeStatus: string
  decodeError?: string | null
  year?: number | null
  make?: string | null
  model?: string | null
  trim?: string | null
  bodyClass?: string | null
  vehicleType?: string | null
  originPlaceId?: string | null
  destinationPlaceId?: string | null
  pickupZip?: string | null
  deliveryZip?: string | null
  inventoryStatus: string
  quoteId?: string | null
  orderId?: string | null
  unitMoveId?: string | null
  loadId?: string | null
  orderNumber?: string | null
  loadNumber?: string | null
  notes?: string | null
  rawInput?: string | null
  metadata?: Record<string, any>
  createdAt?: string
  updatedAt?: string
}

export interface VinBatchDetailResponse {
  batch: VinBatch
  summary: {
    item_count?: number
    decoded_count?: number
    failed_decode_count?: number
    ordered_count?: number
    archived_count?: number
  }
  items: VinInventoryItem[]
  invalidRows: Array<any>
  duplicateRows: Array<any>
}

export interface CarrierProfile {
  id: string
  legal_name: string
  dba_name?: string | null
  dot_number?: string | null
  mc_number?: string | null
  dispatch_contact?: string | null
  accounting_contact?: string | null
  claims_contact?: string | null
  after_hours_contact?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  phone?: string | null
  email?: string | null
  preferred_lanes?: string[] | null
  equipment_types?: string[] | null
  notes?: string | null
  compliance_status?: string | null
  approval_status?: string | null
  created_at?: string
  updated_at?: string
}

export interface CarrierComplianceDocument {
  id: string
  carrier_id: string
  doc_type: string
  status: string
  expiration_date?: string | null
  last_reviewed_at?: string | null
  reviewed_by?: string | null
  notes?: string | null
  created_at?: string
  updated_at?: string
}

function toNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function normalizeQuote(raw: any): Quote {
  return {
    ...raw,
    vehicleYear: toNullableNumber(raw?.vehicleYear),
    estDistanceMiles: toNullableNumber(raw?.estDistanceMiles),
    customerQuoteCents: toNullableNumber(raw?.customerQuoteCents),
    targetCarrierPayCents: toNullableNumber(raw?.targetCarrierPayCents),
    grossMarginCents: toNullableNumber(raw?.grossMarginCents),
    marginPercent: toNullableNumber(raw?.marginPercent),
    customerRatePerMileCents: toNullableNumber(raw?.customerRatePerMileCents),
    carrierRatePerMileCents: toNullableNumber(raw?.carrierRatePerMileCents),
    marginPerMileCents: toNullableNumber(raw?.marginPerMileCents),
  }
}

export type LoadMoney = {
  customerPayCents?: number
  carrierPayCents?: number
  grossMarginCents?: number
  marginPercent?: number
}

export function calculateLoadMargin(customerPayCents?: number, carrierPayCents?: number) {
  const margin =
    typeof customerPayCents === 'number' && typeof carrierPayCents === 'number'
      ? customerPayCents - carrierPayCents
      : null

  const marginPercent =
    margin !== null && customerPayCents && customerPayCents > 0
      ? margin / customerPayCents
      : null

  return { grossMarginCents: margin, marginPercent }
}

export interface Vehicle {
  id: string
  vin: string
  make?: string
  model?: string
  year?: number
  color?: string
  type?: string
  isRunning?: boolean
  hasKeys?: boolean
  damageNotes?: string
}

export interface Job {
  id: string
  jobNumber?: string
  loadId: string
  origin?: string
  destination?: string
  originZip?: string | null
  destinationZip?: string | null
  status: 'ASSIGNED' | 'DRIVER_ASSIGNED' | 'IN_PROGRESS' | 'DELIVERED' | 'CANCELLED' | 'STARTED' | 'PICKED_UP' | 'IN_TRANSIT' | 'WAITING_POD' | 'POD_UPLOADED' | 'INVOICED' | 'PAID' | 'COMPLETED'
  vehicles?: Vehicle[]
  pickupConfirmedAt?: string | null
  deliveryConfirmedAt?: string | null
  proofOfDeliveryUrl?: string | null
  proofOfDeliveryUploadedAt?: string | null
  proofOfDeliveryFilename?: string | null
  proofOfDeliveryMimeType?: string | null
  proofOfDeliverySizeBytes?: number | null
  earnings?: number
  assignedAt?: string
  completedAt?: string | null
}

export interface DemoTrailerLoaderJobResponse {
  loadId: string
  jobId: string
  driverUserId: string
  driverName: string
  equipmentComboKey: string
  status: string
  links: {
    adminTrailerPlanner: string
    driverLoaderPreview: string
    driverJobDetail: string
  }
}

export interface PodDocument {
  id: string | null
  filename: string
  mimeType: string
  sizeBytes: number
  uploadedAt: string | null
  uploadedBy?: string | null
  metadata?: Record<string, unknown>
  downloadUrl?: string | null
}

export interface PodMetadataResponse {
  ok: boolean
  jobId: string
  uploaded: boolean
  file: PodDocument | null
  documents: PodDocument[]
  downloadUrl: string | null
}

export interface PodListResponse {
  ok: boolean
  jobId: string
  count: number
  documents: PodDocument[]
}

export interface Invoice {
  id: string
  invoiceNumber: string
  jobId: string
  loadId?: string
  amount: number
  currency: string
  status: 'PENDING' | 'APPROVED' | 'PAID' | 'FAILED' | 'CANCELLED' | 'DISPUTED'
  dueDate?: string
  createdAt: string
  quickPayOptions?: QuickPayOption[]
}

export interface QuickPayOption {
  type: 'INSTANT' | 'THREE_DAY' | 'THIRTY_DAY'
  feeBps: number
  label: string
  feeAmount: number
  netAmount: number
}

export interface ZipLookupResponse {
  zip: string
  city: string
  state: string
}

export interface VinDecodeResponse {
  vin: string
  valid: boolean
  source: string
  year?: string | null
  make?: string | null
  model?: string | null
  trim?: string | null
  bodyClass?: string | null
  vehicleType?: string | null
  engine?: string | null
  fuelType?: string | null
  error?: string | null
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const api = {
  geo: {
    zipLookup: (zip: string) =>
      postingClient.get<ZipLookupResponse>(`/geo/zip/${encodeURIComponent(zip)}`),
    publicZipLookup: (zip: string) =>
      publicPostingClient.get<ZipLookupResponse>(`/public/geo/zip/${encodeURIComponent(zip)}`),
  },

  vehicles: {
    decodeVin: (vin: string) =>
      postingClient.get<VinDecodeResponse>(`/vehicles/decode-vin/${encodeURIComponent(vin)}`),
  },

  auth: {
    login: (email: string, password: string) =>
      authClient.post<{ token: string; refreshToken: string; userId: string; orgId: string | null; roles: string[] }>('login', { email, password }),
    sandboxConfig: () =>
      postingClient.get<SandboxDemoConfigResponse>('sandbox/demo-session/config'),
    sandboxLogin: (personaKey: string) =>
      postingClient.post<{ token: string; refreshToken: string; userId: string; orgId: string | null; roles: string[]; user?: AuthUser }>('sandbox/demo-session', { personaKey }),
    sandboxMe: () =>
      postingClient.get<AuthUser>('/auth/me'),
    me: () =>
      authClient.get<AuthUser>('me'),
    logout: (refreshToken?: string) =>
      authClient.post('logout', { refreshToken }),
  },

  platformSupport: {
    searchTenants: (q: string) =>
      postingClient.get<PlatformTenantSearchResponse>('/platform/tenant-search', { params: { q } }),
    startSession: (data: { targetTenantOrgId: string; reason: string; expiresInMinutes?: number }) =>
      postingClient.post<{ supportSession: SupportSessionRecord }>('/platform/support-sessions', data),
    endSession: (sessionId: string, reason: string) =>
      postingClient.post<{ supportSession: SupportSessionRecord }>(`/platform/support-sessions/${encodeURIComponent(sessionId)}/end`, { reason }),
    currentSession: () =>
      postingClient.get<{ supportSession: SupportSessionRecord | null }>('/platform/support-sessions/current'),
    auditEvents: (sessionId: string) =>
      postingClient.get<{ events: Array<Record<string, unknown>> }>(`/platform/support-sessions/${encodeURIComponent(sessionId)}/audit-events`),
  },

  quotePort: {
    current: () => postingClient.get<{ quotePort: QuotePortConfig | null }>('/quote-port/current'),
    config: (accountSlug: string) =>
      publicPostingClient.get<{ config: QuotePortConfig }>(`/quote-port/${encodeURIComponent(accountSlug)}/config`),
    submit: (accountSlug: string, data: {
      customerName: string
      customerEmail: string
      customerPhone: string
      pickupAddress1?: string
      pickupAddress2?: string
      pickupCity: string
      pickupState: string
      pickupZip: string
      deliveryAddress1?: string
      deliveryAddress2?: string
      deliveryCity: string
      deliveryState: string
      deliveryZip: string
      requestedPickupDate?: string
      requestedDeliveryDate?: string
      pickupWindowStart?: string
      pickupWindowEnd?: string
      deliveryWindowStart?: string
      deliveryWindowEnd?: string
      vehicles?: Array<{ year?: number; make: string; model: string; vin?: string }>
      vehicleYear?: number
      vehicleMake?: string
      vehicleModel?: string
      vin?: string
      runningStatus?: 'running' | 'not_running' | 'unknown'
      transportType?: 'OPEN' | 'ENCLOSED'
      notes?: string
      consentAccepted: boolean
      requestVinPhotoLater?: boolean
    }) =>
      publicPostingClient.post<{ quoteRequest: QuoteRequest }>(`/quote-port/${encodeURIComponent(accountSlug)}/quotes`, data),
    mileagePreview: (accountSlug: string, data: {
      originZip: string
      destinationZip: string
      originCity?: string
      originState?: string
      destinationCity?: string
      destinationState?: string
    }) => publicPostingClient.post<{ estDistanceMiles: number | null; mileageProvider?: string; mileageSource?: string; warnings?: string[] }>(
      `/quote-port/${encodeURIComponent(accountSlug)}/mileage-preview`, data,
    ),
  },

  quoteRequests: {
    list: async (params?: { status?: string; q?: string; assignmentState?: string }) => {
      const response = await postingClient.get<QuoteRequest[] | { quoteRequests: QuoteRequest[] }>('/quote-requests', { params })
      const raw = response.data
      const quoteRequests: QuoteRequest[] = Array.isArray(raw) ? raw : (raw as any).quoteRequests ?? []
      return { ...response, data: quoteRequests }
    },
    get: async (id: string) => {
      const response = await postingClient.get<QuoteRequest | { quoteRequest: QuoteRequest }>(`/quote-requests/${id}`)
      return { ...response, data: (response.data as any).quoteRequest ?? response.data }
    },
    updateStatus: (id: string, status: QuoteRequest['status']) =>
      postingClient.patch<{ quoteRequest: QuoteRequest }>(`/quote-requests/${id}/status`, { status }),
    assignmentSuggestions: (id: string) =>
      postingClient.get<{
        quoteRequestId: string
        assignmentState: string
        rule: string
        suggestions: QuoteRequestAccountSuggestion[]
      }>(`/quote-requests/${id}/account-suggestions`),
    updateAssignment: (id: string, data: {
      assignmentState: 'unassigned_review' | 'suggested' | 'assigned' | 'rejected' | string
      accountHierarchyId?: string
      locationHierarchyId?: string
      contactHierarchyId?: string
      billingProfileId?: string
    }) => postingClient.patch<{ quoteRequest: QuoteRequest }>(`/quote-requests/${id}/assignment`, data),
    sendQuote: (id: string, data: {
      quotedCustomerPriceCents: number
      quotedCurrency?: string
      quoteMessage?: string
      quoteInternalNotes?: string
      quoteExpiresAt?: string | null
      paymentRequiredBeforePickup?: boolean
      paymentRequirementReason?: string
      accountTermsSnapshot?: Record<string, unknown>
    }) => postingClient.post<{
      quoteRequest: QuoteRequest
      quoteResponseUrl: string
      emailDelivery: QuoteRequestEmailDelivery
    }>(`/quote-requests/${id}/send-quote`, data),
    reject: (id: string, reason: string) =>
      postingClient.post<{
        quoteRequest: QuoteRequest
        emailDelivery: QuoteRequestEmailDelivery
      }>(`/quote-requests/${id}/reject`, { reason }),
    emailOutbox: (id: string) =>
      postingClient.get<{ emailOutbox: Array<{
        id: string
        emailType: string
        toEmail: string
        subject: string
        status: string
        provider: string
        providerMessageId?: string | null
        attempts: number
        lastError?: string | null
        sentAt?: string | null
        createdAt: string
        updatedAt: string
      }> }>(`/quote-requests/${id}/email-outbox`),
    timeline: (id: string) =>
      postingClient.get<{ quoteRequestId: string; orderId?: string | null; timeline: UnifiedTimelineEntry[] }>(`/quote-requests/${id}/timeline`),
  },

  quoteResponse: {
    get: (token: string) =>
      publicPostingClient.get<{ quoteResponse: QuoteResponseView }>(`/quote-response/${encodeURIComponent(token)}`),
    accept: (token: string) =>
      publicPostingClient.post<{
        quoteResponse: QuoteResponseView
        orderDraft?: CustomerPortalOrder
        customerPortalUrl?: string
        nextStep?: string
      }>(`/quote-response/${encodeURIComponent(token)}/accept`),
    decline: (token: string, reason?: string) =>
      publicPostingClient.post<{ quoteResponse: QuoteResponseView }>(`/quote-response/${encodeURIComponent(token)}/decline`, reason ? { reason } : {}),
  },

  customerPortal: {
    get: (token: string) =>
      publicPostingClient.get<CustomerPortalResponse>(`/customer-portal/${encodeURIComponent(token)}`),
    updateOrder: (token: string, data: Record<string, unknown>) =>
      publicPostingClient.patch<CustomerPortalResponse>(`/customer-portal/${encodeURIComponent(token)}/order`, data),
    acknowledgeAgreement: (token: string) =>
      publicPostingClient.post<CustomerPortalResponse>(`/customer-portal/${encodeURIComponent(token)}/agreement`, {}),
    submitOrder: (token: string) =>
      publicPostingClient.post<CustomerPortalResponse>(`/customer-portal/${encodeURIComponent(token)}/submit`, {}),
    timeline: (token: string) =>
      publicPostingClient.get<{ orderId?: string | null; timeline: UnifiedTimelineEntry[] }>(`/customer-portal/${encodeURIComponent(token)}/timeline`),
  },

  loads: {
    list: async (params?: { status?: string; carrierId?: string; viewer?: 'carrier' | 'driver' | 'internal' }) => {
      const r = await postingClient.get<Load[] | { loads: Load[] }>('/loads', { params })
      const raw = r.data
      const loads: Load[] = Array.isArray(raw) ? raw : (raw as any).loads ?? []
      return { ...r, data: loads }
    },
    get: async (id: string, params?: { viewer?: 'carrier' | 'driver' | 'internal' }) => {
      const response = await postingClient.get<Load | { load: Load }>(`/loads/${id}`, { params })
      return { ...response, data: (response.data as any).load ?? response.data }
    },
    create: (data: {
      origin?: string
      destination?: string
      pickupDate?: string
      deliveryDate?: string
      originZip?: string
      originCity?: string
      originState?: string
      destinationZip?: string
      destinationCity?: string
      destinationState?: string
      estDistanceMiles?: number
      miles?: number
      customerPay?: number
      carrierPay?: number
      customerPayCents?: number
      carrierPayCents?: number
      totalPayment?: number
      totalPay?: number
      rate?: number
      price?: number
      payment?: number
      notes?: string
      vehicles: Array<{ vin: string; make?: string; model?: string; year?: number; color?: string; type?: string; isRunning?: boolean; hasKeys?: boolean }>
    }) => postingClient.post<{ loadId: string; loadNumber: string }>('/loads', data),
    claim: (loadId: string, data?: { carrierOrgId?: string; notes?: string }) =>
      postingClient.post<{ loadId: string; status: string }>(`/loads/${loadId}/claim`, data ?? {}),
    post: (loadId: string, data?: { notes?: string }) =>
      postingClient.post<{ loadId: string; status: string }>(`/loads/${loadId}/post`, data ?? {}),
    assignCarrier: (loadId: string, data: {
      carrierId?: string
      carrierOrgId?: string
      driverUserId?: string
      truckNumber?: string
      trailerNumber?: string
      notes?: string
      carrierPayCents?: number
      overrideCompliance?: boolean
    }) =>
      postingClient.post<{ loadId: string; jobId: string; jobNumber: string; status: string }>(`/loads/${loadId}/assign-carrier`, data),
    assignDriver: (loadId: string, data: {
      driverId?: string
      driverUserId?: string
      carrierId?: string
      carrierOrgId?: string
      truckNumber?: string
      trailerNumber?: string
      notes?: string
    }) =>
      postingClient.post<{ loadId: string; jobId: string; jobNumber?: string; status: string; driverUserId: string }>(`/loads/${loadId}/assign-driver`, data),
    unassign: (loadId: string, data: { target: 'carrier' | 'driver' | 'both'; reason?: string }) =>
      postingClient.post<{ loadId: string; status: string; target: string }>(`/loads/${loadId}/unassign`, data),
    assignmentOptions: (loadId: string) =>
      postingClient.get<LoadAssignmentOptionsResponse>(`/loads/${loadId}/assignment-options`),
    assignmentHistory: (loadId: string) =>
      postingClient.get<{ loadId: string; history: LoadAssignmentHistoryEvent[] }>(`/loads/${loadId}/assignment-history`),
    updateCarrierOffer: (loadId: string, data: { carrierOfferCents: number; notes?: string }) =>
      postingClient.patch<{ loadId: string; carrierOfferCents: number }>(`/loads/${loadId}/carrier-offer`, data),
    updatePlanning: (loadId: string, data: {
      status?: string
      notes?: string
      origin?: string
      destination?: string
      originState?: string
      destinationState?: string
      originZip?: string
      destinationZip?: string
      pickupDate?: string
      deliveryDate?: string
      estDistanceMiles?: number
    }) => postingClient.patch<{ load: Load }>(`/loads/${loadId}/planning`, data),
    getTrailerPlan: (loadId: string) =>
      postingClient.get<LoadTrailerPlanResponse>(`/loads/${loadId}/trailer-plan`),
    saveTrailerPlan: (
      loadId: string,
      data: {
        plannerStatus?: string
        plannerNotes?: string
        equipmentLayoutType?: string | null
        trailerDiagramMode?: string | null
        leverHeights?: Record<string, number | null>
        deckStates?: Record<string, { height: number; angle: number }>
        rampStates?: Record<string, 'stored' | 'deployed' | 'raised' | 'lowered'>
        warnings?: string[]
        slotProfiles?: Record<string, {
          maxLengthInches?: number
          maxHeightInches?: number
          maxWeightLbs?: number
          allowInoperable?: boolean
        }>
        tripLayouts?: Array<{
          id: string
          name: string
          createdAt: string
          createdBy?: string | null
          vehicles: Array<{
            vehicleId: string
            trailerPosition: string | null
            pickupSequence: number | null
            deliverySequence: number | null
            notes: string | null
            operable: boolean | null
          }>
          leverHeights: Record<string, number>
          slotProfiles: Record<string, {
            maxLengthInches?: number
            maxHeightInches?: number
            maxWeightLbs?: number
            allowInoperable?: boolean
          }>
        }>
        activeTripLayoutId?: string | null
        vehicles?: Array<{
          vehicleId: string
          trailerPosition?: string | null
          pickupSequence?: number | null
          deliverySequence?: number | null
          notes?: string | null
        }>
      }
    ) => postingClient.patch<LoadTrailerPlanResponse>(`/loads/${loadId}/trailer-plan`, data),
    autoSequenceTrailerPlan: (loadId: string, data?: { vehicleOrder?: string[] }) =>
      postingClient.post<LoadTrailerPlanResponse>(`/loads/${loadId}/trailer-plan/auto-sequence`, data ?? {}),
    recommendTrailerPlan: (loadId: string, data?: { apply?: boolean; name?: string }) =>
      postingClient.post<LoadTrailerRecommendationResponse>(`/loads/${loadId}/trailer-plan/recommendation`, data ?? {}),
    saveTripLayoutSnapshot: (loadId: string, data: { name: string }) =>
      postingClient.post<{ ok: boolean; layoutId: string; name: string }>(`/loads/${loadId}/trailer-plan/layouts/save`, data),
    applyTripLayoutSnapshot: (loadId: string, layoutId: string) =>
      postingClient.post<LoadTrailerPlanResponse>(`/loads/${loadId}/trailer-plan/layouts/${layoutId}/apply`, {}),
    getDriverTrailerPlanView: (loadId: string) =>
      postingClient.get<any>(`/driver/loads/${loadId}/trailer-plan-view`),
    magicTrailerPlan: (loadId: string) =>
      postingClient.post<any>(`/loads/${loadId}/trailer-plan/magic`),
    applyMagicTrailerPlan: (loadId: string, data: any) =>
      postingClient.post<any>(`/loads/${loadId}/trailer-plan/apply-magic`, data),
    cancel: (loadId: string, reason?: string) =>
      postingClient.post(`/loads/${loadId}/cancel`, { reason }),
    delete: (loadId: string) =>
      postingClient.delete<{ loadId: string; deleted: boolean; status?: string }>(`/loads/${loadId}`),
  },

  quotes: {
    list: async (params?: { status?: string; q?: string }) => {
      const response = await postingClient.get<Quote[] | { quotes: Quote[] }>('/quotes', { params })
      const raw = response.data
      const quotes: Quote[] = (Array.isArray(raw) ? raw : (raw as any).quotes ?? []).map(normalizeQuote)
      return { ...response, data: quotes }
    },
    get: async (quoteId: string) => {
      const response = await postingClient.get<Quote | { quote: Quote }>(`/quotes/${quoteId}`)
      return { ...response, data: normalizeQuote((response.data as any).quote ?? response.data) }
    },
    create: (data: Partial<Quote>) =>
      postingClient.post<{ quote: Quote }>('/quotes', data).then((response) => ({
        ...response,
        data: { ...response.data, quote: normalizeQuote(response.data.quote) },
      })),
    update: (quoteId: string, data: Partial<Quote>) =>
      postingClient.patch<{ quote: Quote }>(`/quotes/${quoteId}`, data).then((response) => ({
        ...response,
        data: { ...response.data, quote: normalizeQuote(response.data.quote) },
      })),
    convertToDraftLoad: (quoteId: string) =>
      postingClient.post<{ converted: boolean; loadId: string; loadNumber?: string; quote: Quote }>(`/quotes/${quoteId}/convert-to-draft-load`).then((response) => ({
        ...response,
        data: { ...response.data, quote: normalizeQuote(response.data.quote) },
      })),
    convertToOrder: (quoteId: string) =>
      postingClient.post<{ converted: boolean; message?: string; order: Order | null; unitMoves?: UnitMove[]; statusTimeline?: OrderStatusEvent[] }>(`/quotes/${quoteId}/convert-to-order`),
    delete: (quoteId: string) =>
      postingClient.delete<{ quoteId: string; deleted: boolean; convertedLoadId?: string | null; quote: Quote }>(`/quotes/${quoteId}`).then((response) => ({
        ...response,
        data: {
          ...response.data,
          quote: normalizeQuote(response.data.quote),
        },
      })),
  },

  orders: {
    list: async (params?: { status?: string; q?: string }) => {
      const response = await postingClient.get<Order[] | { orders: Order[] }>('/orders', { params })
      const raw = response.data
      const orders: Order[] = Array.isArray(raw) ? raw : (raw as any).orders ?? []
      return { ...response, data: orders }
    },
    get: async (orderId: string) => {
      const response = await postingClient.get<{ order: Order; unitMoves: UnitMove[]; statusTimeline: OrderStatusEvent[] }>(`/orders/${orderId}`)
      return response
    },
    create: (data: any) =>
      postingClient.post<{ order: Order; unitMoves: UnitMove[]; statusTimeline: OrderStatusEvent[] }>('/orders', data),
    update: (orderId: string, data: any) =>
      postingClient.patch<{ order: Order; unitMoves: UnitMove[]; statusTimeline: OrderStatusEvent[] }>(`/orders/${orderId}`, data),
    postAsLoad: (orderId: string) =>
      postingClient.post<{ posted: boolean; loadId: string; loadNumber?: string; message?: string }>(`/orders/${orderId}/post-as-load`),
    assignDirect: (orderId: string, data: { carrierOrgId?: string; carrierId?: string; driverUserId?: string; truckNumber?: string; trailerNumber?: string }) =>
      postingClient.post<{ orderId: string; loadId: string; jobId: string; jobNumber: string; status: string }>(`/orders/${orderId}/assign-direct`, data),
    getDispatchTrip: (orderId: string) =>
      postingClient.get<DispatchTripBundle>(`/orders/${orderId}/dispatch-trip`),
    upsertDispatchTrip: (
      orderId: string,
      data: {
        carrierId?: string
        carrierOrgId?: string
        driverUserId?: string
        trailerNumber?: string
        trailerId?: string
        trailerLayoutRef?: string
        assignmentNotes?: string
      }
    ) => postingClient.post<DispatchTripBundle>(`/orders/${orderId}/dispatch-trip`, data),
    updateDispatchTripStop: (
      orderId: string,
      stopId: string,
      data: {
        dateWindowStart?: string
        dateWindowEnd?: string
        appointmentRequired?: boolean
        appointmentStatus?: string
        appointmentConfirmed?: boolean
        confirmationNumber?: string
        accessInstructions?: string
        lotNotes?: string
        contactName?: string
        contactPhone?: string
        afterHoursAllowed?: boolean
        rescheduleState?: string
        stopStatus?: string
        arrivedAt?: string
        completedAt?: string
      }
    ) => postingClient.patch<DispatchTripBundle>(`/orders/${orderId}/dispatch-trip/stops/${stopId}`, data),
    dispatchTrip: (orderId: string) =>
      postingClient.post<DispatchTripBundle>(`/orders/${orderId}/dispatch-trip/dispatch`),
    unifiedTimeline: (orderId: string) =>
      postingClient.get<{ orderId: string; timeline: UnifiedTimelineEntry[] }>(`/orders/${orderId}/unified-timeline`),
  },

  dispatchLoads: {
    materializeOrder: (loadId: string) =>
      postingClient.post<{ created: boolean; order: Order }>(`/loads/${loadId}/dispatch-order`),
  },

  vehicleInspections: {
    createPickup: (data: { orderId: string; unitId: string; loadId?: string; tripId?: string; expiresInHours?: number }) =>
      jobClient.post<{ inspectionId: string; inspectionType: 'PICKUP'; status: string; publicLink: string; publicTokenExpiresAt: string }>(`/v1/inspections/pickup`, data),
    createDelivery: (data: { orderId: string; unitId: string; loadId?: string; tripId?: string; expiresInHours?: number }) =>
      jobClient.post<{ inspectionId: string; inspectionType: 'DELIVERY'; status: string; publicLink: string; publicTokenExpiresAt: string }>(`/v1/inspections/delivery`, data),
    driverTodayTripEntry: (stopId: string, data?: { unitId?: string; expiresInHours?: number }) =>
      jobClient.post<DriverInspectionEntryResponse>(`/v1/driver/today-trip/stops/${stopId}/inspection-entry`, data ?? {}),
    reissueLink: (inspectionId: string, data?: { expiresInHours?: number }) =>
      jobClient.post<{ inspectionId: string; status: string; publicLink: string; publicTokenExpiresAt: string }>(`/v1/inspections/${inspectionId}/reissue-link`, data ?? {}),
    timeline: (unitId: string) =>
      jobClient.get<{ unitId: string; inspections: VehicleInspectionSummary[] }>(`/v1/units/${unitId}/inspections/timeline`),
    detail: (inspectionId: string) =>
      jobClient.get<any>(`/v1/inspections/${inspectionId}`),
    voidInspection: (inspectionId: string, reason: string) =>
      jobClient.post(`/v1/inspections/${inspectionId}/void`, { reason }),
    documents: (inspectionId: string) =>
      jobClient.get<{ inspectionId: string; photos: any[]; signatures: any[] }>(`/v1/inspections/${inspectionId}/documents`),
    reviewException: (inspectionId: string, exceptionId: string, resolutionStatus: string) =>
      jobClient.post(`/v1/inspections/${inspectionId}/exceptions/${exceptionId}/review`, { resolutionStatus }),
    publicSession: (publicToken: string) =>
      publicPostingClient.get<VehicleInspectionPublicSession>(`/inspect/${publicToken}/session`),
    publicBegin: (publicToken: string, data: any) =>
      publicPostingClient.post(`/inspect/${publicToken}/begin`, data),
    publicUploadPhoto: (publicToken: string, file: File, meta: { photoCategory: string; panelOrArea?: string; sortOrder?: number }) => {
      const form = new FormData()
      form.append('file', file)
      const params = new URLSearchParams()
      params.set('photoCategory', meta.photoCategory)
      if (meta.panelOrArea) params.set('panelOrArea', meta.panelOrArea)
      if (typeof meta.sortOrder === 'number') params.set('sortOrder', String(meta.sortOrder))
      return publicPostingClient.post(`/inspect/${publicToken}/photos?${params.toString()}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    publicMarkers: (publicToken: string, markers: any[]) =>
      publicPostingClient.put(`/inspect/${publicToken}/markers`, { markers }),
    publicAddException: (publicToken: string, data: { category: string; severity: string; description: string }) =>
      publicPostingClient.post(`/inspect/${publicToken}/exceptions`, data),
    publicReportIssue: (
      publicToken: string,
      data: {
        reportedArea: string
        description: string
        severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN'
        reporterRole?: string
        reporterName?: string
        markerRefs?: any[]
        exceptionRefs?: any[]
        evidenceRefs?: any[]
      }
    ) => publicPostingClient.post<{ issue: ConditionIssue }>(`/inspect/${publicToken}/issues`, data),
    publicSubmit: (publicToken: string, data: any) =>
      publicPostingClient.post(`/inspect/${publicToken}/submit`, data),
    publicSignature: (publicToken: string, data: { signerName: string; signerRole: string; signerType: string; signatureData: string }) =>
      publicPostingClient.post(`/inspect/${publicToken}/signature`, data),
    publicLock: (publicToken: string) =>
      publicPostingClient.post(`/inspect/${publicToken}/lock`),
    issuesForInspection: (inspectionId: string) =>
      jobClient.get<{ inspectionId: string; issues: ConditionIssue[] }>(`/v1/inspections/${inspectionId}/issues`),
    carrierIssues: (params?: { status?: string }) =>
      jobClient.get<{ issues: ConditionIssue[] }>('/v1/carrier/issues', { params }),
    updateIssueStatus: (issueId: string, data: { status: string; internalNotes?: string }) =>
      jobClient.patch<{ issue: ConditionIssue }>(`/v1/issues/${issueId}/status`, data),
    addIssueCarrierNote: (issueId: string, data: { note: string }) =>
      jobClient.patch<{ issue: ConditionIssue }>(`/v1/issues/${issueId}/carrier-note`, data),
  },

  search: {
    global: (q: string) =>
      postingClient.get<GlobalSearchResult>('/search', { params: { q } }),
  },

  dispatchBoard: {
    get: (params?: {
      customerId?: string
      laneId?: string
      originState?: string
      destinationState?: string
      pickupZip?: string
      deliveryZip?: string
      status?: string
      q?: string
      limit?: number
    }) => postingClient.get<DispatchBoardSummary>('/dispatch-board', { params }),
    candidates: (params?: {
      customerId?: string
      laneId?: string
      originState?: string
      destinationState?: string
      pickupZip?: string
      deliveryZip?: string
      status?: string
      q?: string
      limit?: number
    }) => postingClient.get<{ candidates: DispatchBoardCandidate[]; totals: DispatchBoardSummary['totals'] }>('/dispatch-board/candidates', { params }),
    previewLoad: (data: {
      selectedUnitMoveIds?: string[]
      selectedVinInventoryItemIds?: string[]
      customerId?: string
      laneId?: string
      pickupZip?: string
      deliveryZip?: string
      originState?: string
      destinationState?: string
      carrierPayCents?: number
      customerPriceCents?: number
      estDistanceMiles?: number
    }) => postingClient.post<DispatchBoardPreview>('/dispatch-board/preview-load', data),
    createLoad: (data: {
      selectedUnitMoveIds?: string[]
      selectedVinInventoryItemIds?: string[]
      customerId?: string
      laneId?: string
      origin?: string
      destination?: string
      pickupCity?: string
      pickupZip?: string
      originState?: string
      deliveryCity?: string
      deliveryZip?: string
      destinationState?: string
      pickupDate?: string
      deliveryDate?: string
      carrierPayCents?: number
      customerPriceCents?: number
      postToBoard?: boolean
      assignCarrierId?: string
      assignDriverId?: string
      dispatchNotes?: string
      estDistanceMiles?: number
    }) => postingClient.post<DispatchBoardCreateLoadResult>('/dispatch-board/create-load', data),
  },

  customers: {
    list: async (params?: { q?: string; customerType?: string; status?: string }) => {
      const response = await postingClient.get<Customer[] | { customers: Customer[] }>('/customers', { params })
      const raw = response.data
      const customers: Customer[] = Array.isArray(raw) ? raw : (raw as any).customers ?? []
      return { ...response, data: customers }
    },
    get: (id: string) => postingClient.get<{ customer: Customer; relatedOrders?: Array<any> }>(`/customers/${id}`),
    create: (data: any) => postingClient.post<{ customer: Customer }>('/customers', data),
    update: (id: string, data: any) => postingClient.patch<{ customer: Customer }>(`/customers/${id}`, data),
    remove: (id: string) => postingClient.delete<{ customerId: string; deleted: boolean }>(`/customers/${id}`),
  },

  accountHierarchy: {
    search: (params?: { q?: string; accountId?: string }) =>
      postingClient.get<{
        accounts: AccountHierarchyAccount[]
        locations: AccountHierarchyLocation[]
        contacts: AccountHierarchyContact[]
        billingProfiles: AccountHierarchyBillingProfile[]
      }>('/account-hierarchy/search', { params }),
    createAccount: (data: {
      customerId?: string
      accountCode?: string
      legalName: string
      displayName?: string
      status?: string
      creditHold?: boolean
      creditHoldReason?: string
      paymentTerms?: string
      metadata?: Record<string, unknown>
    }) => postingClient.post<{ account: AccountHierarchyAccount }>('/account-hierarchy/accounts', data),
    createLocation: (accountId: string, data: {
      placeId?: string
      locationCode?: string
      name: string
      status?: string
      address1?: string
      address2?: string
      city?: string
      state?: string
      zip?: string
      country?: string
      metadata?: Record<string, unknown>
    }) => postingClient.post<{ location: AccountHierarchyLocation }>(`/account-hierarchy/accounts/${accountId}/locations`, data),
    createContact: (accountId: string, data: {
      locationId?: string
      fullName: string
      email?: string
      phone?: string
      roleTitle?: string
      isPrimary?: boolean
      status?: string
      metadata?: Record<string, unknown>
    }) => postingClient.post<{ contact: AccountHierarchyContact }>(`/account-hierarchy/accounts/${accountId}/contacts`, data),
    createBillingProfile: (accountId: string, data: {
      profileName: string
      status?: string
      paymentTerms?: string
      creditHold?: boolean
      creditHoldReason?: string
      billingContactName?: string
      billingContactEmail?: string
      billingContactPhone?: string
      billingAddress1?: string
      billingAddress2?: string
      billingCity?: string
      billingState?: string
      billingZip?: string
      billingCountry?: string
      metadata?: Record<string, unknown>
    }) => postingClient.post<{ billingProfile: AccountHierarchyBillingProfile }>(`/account-hierarchy/accounts/${accountId}/billing-profiles`, data),
  },

  places: {
    list: async (params?: { q?: string; placeType?: string; status?: string; state?: string; zip?: string }) => {
      const response = await postingClient.get<Place[] | { places: Place[] }>('/places', { params })
      const raw = response.data
      const places: Place[] = Array.isArray(raw) ? raw : (raw as any).places ?? []
      return { ...response, data: places }
    },
    get: (id: string) => postingClient.get<{ place: Place; relatedOrders?: Array<any> }>(`/places/${id}`),
    create: (data: any) => postingClient.post<{ place: Place }>('/places', data),
    update: (id: string, data: any) => postingClient.patch<{ place: Place }>(`/places/${id}`, data),
    remove: (id: string) => postingClient.delete<{ placeId: string; deleted: boolean }>(`/places/${id}`),
  },

  lanes: {
    list: async (params?: { q?: string; status?: string; customerId?: string }) => {
      const response = await postingClient.get<Lane[] | { lanes: Lane[] }>('/lanes', { params })
      const raw = response.data
      const lanes: Lane[] = Array.isArray(raw) ? raw : (raw as any).lanes ?? []
      return { ...response, data: lanes }
    },
    get: (id: string) => postingClient.get<{ lane: Lane }>(`/lanes/${id}`),
    create: (data: any) => postingClient.post<{ lane: Lane }>('/lanes', data),
    update: (id: string, data: any) => postingClient.patch<{ lane: Lane }>(`/lanes/${id}`, data),
    remove: (id: string) => postingClient.delete<{ laneId: string; deleted: boolean }>(`/lanes/${id}`),
    history: (id: string) => postingClient.get<{ laneId: string; laneNumber: string; history: LanePriceHistoryItem[] }>(`/lanes/${id}/history`),
  },

  vinBatches: {
    create: (data: { customerId?: string; laneId?: string; rawText: string; notes?: string; sourceType?: string; sourceName?: string }) =>
      postingClient.post<{
        batch: VinBatch
        summary: { rawCount: number; validCount: number; duplicateCount: number; invalidCount: number; decodedCount: number; failedDecodeCount: number }
        invalidRows: Array<any>
        duplicateRows: Array<any>
      }>('/vin-batches', data),
    list: async (params?: { customerId?: string; laneId?: string; status?: string; q?: string }) => {
      const response = await postingClient.get<VinBatch[] | { batches: VinBatch[] }>('/vin-batches', { params })
      const raw = response.data
      const batches: VinBatch[] = Array.isArray(raw) ? raw : (raw as any).batches ?? []
      return { ...response, data: batches }
    },
    get: (id: string) => postingClient.get<VinBatchDetailResponse>(`/vin-batches/${id}`),
    update: (id: string, data: any) => postingClient.patch<{ batch: VinBatch }>(`/vin-batches/${id}`, data),
    remove: (id: string) => postingClient.delete<{ batchId: string; deleted: boolean }>(`/vin-batches/${id}`),
  },

  vinInventory: {
    list: async (params?: {
      q?: string
      customerId?: string
      laneId?: string
      status?: string
      decodeStatus?: string
      make?: string
      model?: string
      pickupZip?: string
      deliveryZip?: string
      limit?: number
    }) => {
      const response = await postingClient.get<VinInventoryItem[] | { items: VinInventoryItem[] }>('/vin-inventory', { params })
      const raw = response.data
      const items: VinInventoryItem[] = Array.isArray(raw) ? raw : (raw as any).items ?? []
      return { ...response, data: items }
    },
    update: (id: string, data: any) => postingClient.patch<{ item: VinInventoryItem }>(`/vin-inventory/${id}`, data),
    retryDecode: (id: string) => postingClient.post<{ item: VinInventoryItem }>(`/vin-inventory/${id}/decode-retry`),
    bulkAction: (data: { itemIds: string[]; action: string; laneId?: string; customerId?: string }) =>
      postingClient.post<{ action: string; updatedCount: number; items: VinInventoryItem[] }>('/vin-inventory/bulk-action', data),
    convertToOrder: (data: { itemIds: string[]; customerId?: string; laneId?: string; notes?: string }) =>
      postingClient.post<{ order: Order; unitMoves: UnitMove[]; convertedCount: number }>('/vin-inventory/convert-to-order', data),
  },

  carriers: {
    list: async (params?: { q?: string; status?: string }) => {
      const response = await postingClient.get<CarrierProfile[] | { carriers: CarrierProfile[] }>('/carriers', { params })
      const raw = response.data
      const carriers: CarrierProfile[] = Array.isArray(raw) ? raw : (raw as any).carriers ?? []
      return { ...response, data: carriers }
    },
    create: (data: any) => postingClient.post<{ carrier: CarrierProfile }>('/carriers', data),
    get: (id: string) => postingClient.get<{ carrier: CarrierProfile }>(`/carriers/${id}`),
    update: (id: string, data: any) => postingClient.patch<{ carrier: CarrierProfile }>(`/carriers/${id}`, data),
    compliance: (id: string) => postingClient.get<{ complianceStatus: string; documents: CarrierComplianceDocument[] }>(`/carriers/${id}/compliance`),
    addComplianceDoc: (id: string, data: any) => postingClient.post<{ document: CarrierComplianceDocument; complianceStatus: string }>(`/carriers/${id}/compliance`, data),
    updateComplianceDoc: (id: string, docId: string, data: any) => postingClient.patch<{ document: CarrierComplianceDocument; complianceStatus: string }>(`/carriers/${id}/compliance/${docId}`, data),
  },

  jobs: {
    list: async (params?: { status?: string; viewer?: 'carrier' | 'driver' | 'internal' }) => {
      const r = await jobClient.get<Job[] | { jobs: Job[] }>('/jobs', { params })
      const raw = r.data
      const jobs: Job[] = Array.isArray(raw) ? raw : (raw as any).jobs ?? []
      return { ...r, data: jobs }
    },
    get: async (id: string, params?: { viewer?: 'carrier' | 'driver' | 'internal' }) => {
      const response = await jobClient.get<Job | { job: Job }>(`/jobs/${id}`, { params })
      return { ...response, data: (response.data as any).job ?? response.data }
    },
    pickup: (id: string, data?: { lat?: number; lng?: number; notes?: string }) =>
      jobClient.post<Job>(`/jobs/${id}/pickup`, data ?? {}),
    assignDriver: (id: string, driverUserId: string) =>
      jobClient.post<Job>(`/jobs/${id}/assign-driver`, { driverUserId }),
    updateStatus: (id: string, status: string) =>
      jobClient.put<{ success: true; status: string }>(`/jobs/${id}/status`, { status }),
    deliver: (id: string, data: { lat?: number; lng?: number; podUrl?: string; notes?: string }) =>
      jobClient.post<Job>(`/jobs/${id}/deliver`, data),
    complete: (jobId: string, data?: { miles?: number; revenue?: number; carrierPay?: number; accessorials?: Record<string, number>; quickPayOption?: 'INSTANT' | 'THREE_DAY' | 'THIRTY_DAY' }) =>
      jobClient.patch(`/jobs/${jobId}/complete`, data ?? {}),
    createTrailerLoaderDemo: () =>
      jobClient.post<DemoTrailerLoaderJobResponse>('/demo/trailer-loader-job'),
    uploadPod: async (id: string, file: File) => {
      const form = new FormData()
      form.append('pod', file)
      return jobClient.post<{ ok: true; jobId: string; proofOfDeliveryUrl: string; documentId: string; file: { id: string; filename: string; mimeType: string; sizeBytes: number; uploadedAt: string } }>(
        `/jobs/${id}/pod`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    },
    getPod: (id: string) =>
      jobClient.get<PodMetadataResponse>(`/jobs/${id}/pod`),
    listPodDocuments: (id: string) =>
      jobClient.get<PodListResponse>(`/jobs/${id}/pod/list`),
    downloadPod: (id: string, options?: { documentId?: string; download?: boolean }) => {
      const search = new URLSearchParams()
      if (options?.documentId) search.set('documentId', options.documentId)
      if (options?.download) search.set('download', '1')
      const suffix = search.toString() ? `?${search.toString()}` : ''
      return jobClient.get<Blob>(`/jobs/${id}/pod/file${suffix}`, { responseType: 'blob' })
    },
    deletePodDocument: (id: string, documentId: string) =>
      jobClient.delete<{ ok: true; jobId: string; deletedDocumentId: string; latest: PodDocument | null }>(`/jobs/${id}/pod/${documentId}`),
    cancel: (id: string, reason?: string) =>
      jobClient.post(`/jobs/${id}/cancel`, { reason }),
  },

  driver: {
    todayTrip: () => postingClient.get<DispatchTripBundle>('/driver/today-trip'),
    todayTimeline: () => postingClient.get<{ orderId?: string | null; tripId?: string | null; timeline: UnifiedTimelineEntry[] }>('/driver/today-trip/timeline'),
    updateTodayTripStopAppointment: (
      stopId: string,
      data: {
        appointmentStatus: 'REQUESTED' | 'CONFIRMED' | 'RESCHEDULE_REQUESTED' | 'MISSED' | 'COMPLETED'
        appointmentConfirmed?: boolean
        confirmationNumber?: string
        notes?: string
      }
    ) => postingClient.patch<DispatchTripBundle>(`/driver/today-trip/stops/${stopId}/appointment`, data),
    performTodayTripStopAction: (
      stopId: string,
      data: {
        action:
          | 'ACKNOWLEDGE'
          | 'EN_ROUTE'
          | 'MARK_ARRIVAL'
          | 'START_PICKUP'
          | 'LOCK_PICKUP_INSPECTION'
          | 'CONFIRM_LOADED'
          | 'REPORT_CONDITION_ISSUE'
          | 'START_DELIVERY'
          | 'COMPLETE_DELIVERY'
          | 'MARK_UNABLE_TO_COMPLETE'
        unitMoveId?: string | null
        notes?: string
      }
    ) => postingClient.patch<DispatchTripBundle>(`/driver/today-trip/stops/${stopId}/action`, data),
  },

  invoices: {
    list: async (params?: { jobId?: string; loadId?: string }) => {
      const r = await postingClient.get<Invoice[] | { invoices: Invoice[] }>('/invoices', { params })
      const raw = r.data
      const invoices: Invoice[] = Array.isArray(raw) ? raw : (raw as any).invoices ?? []
      return { ...r, data: invoices }
    },
    get: async (id: string) => {
      const response = await postingClient.get<Invoice | { invoice: Invoice }>(`/invoices/${id}`)
      return { ...response, data: (response.data as any).invoice ?? response.data }
    },
    markPaid: (id: string) =>
      postingClient.patch<{ invoiceId: string; status: string }>(`/invoices/${id}/paid`),
  },

  trailerLayout: {
    get: (jobId: string) =>
      jobClient.get<TrailerLayoutResponse>(`/jobs/${jobId}/trailer-layout`),
    save: (jobId: string, assignments: Array<{ slotId: string; vehicleId: string }>) =>
      jobClient.patch<TrailerLayoutResponse>(`/jobs/${jobId}/trailer-layout`, { assignments }),
    setDriverPermission: (jobId: string, body: { driverEditUnlocked: boolean; reason?: string }) =>
      jobClient.patch<{ ok: boolean; jobId: string; driverEditPermission: any }>(`/jobs/${jobId}/trailer-layout/driver-permission`, body),
    autoArrange: (jobId: string) =>
      jobClient.post<AutoArrangeResponse>(`/jobs/${jobId}/trailer-layout/auto-arrange`),
    updateProfile: (jobId: string, profile: Partial<EquipmentProfile>) =>
      jobClient.patch<TrailerProfileUpdateResponse>(`/jobs/${jobId}/trailer-profile`, profile),
    applySpec: (jobId: string, specId: string) =>
      jobClient.post<TrailerProfileApplySpecResponse>(`/jobs/${jobId}/trailer-profile/apply-spec`, { specId }),
    applyCombo: (jobId: string, comboKey: string) =>
      jobClient.post<TrailerProfileApplyComboResponse>(`/jobs/${jobId}/trailer-profile/apply-combo`, { comboKey }),
  },

  equipment: {
    schema: () =>
      jobClient.get<EquipmentSchemaResponse>('/equipment/schema'),
    list: (category: 'truck' | 'trailer', params?: { manufacturer?: string; model?: string; q?: string; limit?: number }) =>
      jobClient.get<EquipmentSpecListResponse>(`/equipment/specs/${category}`, { params }),
    get: (category: 'truck' | 'trailer', id: string) =>
      jobClient.get<{ spec: EquipmentSpec }>(`/equipment/specs/${category}/${id}`),
    refresh: () =>
      jobClient.post<EquipmentRefreshResponse>('/equipment/refresh'),
    combos: (params?: { rigType?: string; q?: string; limit?: number }) =>
      jobClient.get<EquipmentComboListResponse>('/equipment/combos', { params }),
    comboByKey: (comboKey: string) =>
      jobClient.get<{ combo: EquipmentCombo; tractorSpec: Record<string, unknown> | null; trailerSpec: Record<string, unknown> | null }>(`/equipment/combos/by-key/${comboKey}`),
  },

  vehicleFit: {
    getByVin: (vin: string) =>
      jobClient.get<{ profile: VehicleFitProfile }>(`/vehicles/fit-profile/${vin}`),
    resolve: (input: { vin?: string; year?: number; make?: string; model?: string; trim?: string }) =>
      jobClient.post<VehicleFitProfileResult>('/vehicles/fit-profile/resolve', input),
    resolveJobProfiles: (jobId: string) =>
      jobClient.post<VehicleFitResolveJobResponse>(`/jobs/${jobId}/vehicles/resolve-fit-profiles`),
    jobSummary: (jobId: string) =>
      jobClient.get<VehicleFitSummaryResponse>(`/jobs/${jobId}/vehicle-fit-summary`),
  },
}

// ─── Trailer Types ────────────────────────────────────────────────────────────
export interface TrailerVehicle {
  vehicleId: string
  vin: string
  make?: string
  model?: string
  year?: number
  color?: string
  bodyStyle?: string
  slotId?: string | null
}

export interface TrailerSlotMeta {
  slotId: string
  maxLength: number
  maxHeight: number
  maxWeight: number
  isUpper: boolean
  sortOrder: number
}

export interface EquipmentProfile {
  tractorSteerAxles: number
  tractorDriveAxles: number
  tractorSteerAxleLimit: number | null
  tractorDriveAxleLimit: number | null
  tractorWheelbase: number | null
  tractorGcwr: number | null
  tractorEmptyWeight: number | null
  trailerAxles: number
  trailerAxleLimit: number | null
  trailerDeckLength: number
  trailerDeckWidth: number | null
  trailerDeckHeight: number
  trailerRampLength: number | null
  trailerEmptyWeight: number | null
  isDoubleDeck: boolean
  overrideGcwrLimit: number | null
  overrideAxleLimits: boolean
}

export interface AutoHaulerSlotFitRules {
  maxVehicleLengthIn?: number | null
  maxVehicleHeightIn?: number | null
  maxVehicleWeightLb?: number | null
  allowedVehicleTypes?: string[] | null
  requiresOperable?: boolean | null
}

export interface AutoHaulerSlot {
  slotKey: string
  label: string
  zoneKey: string
  displayOrder: number
  deckLevel: 'upper' | 'lower' | 'tractor' | 'belly' | 'unknown'
  xNorm?: number
  yNorm?: number
  widthNorm?: number
  heightNorm?: number
  fitRules?: AutoHaulerSlotFitRules
  riskRules?: Record<string, boolean>
}

export interface AutoHaulerZone {
  zoneKey: string
  label: string
  zoneType: string
  deckLevel: string
  approximateLengthIn?: number | null
  approximateClearanceHeightIn?: number | null
  maxVehicleLengthIn?: number | null
  maxVehicleWeightLb?: number | null
  hydraulic?: boolean
  notes?: string[]
}

export interface AutoHaulerGeometry {
  rigType: string
  nominalVehicleCapacity: number
  zones: AutoHaulerZone[]
  slotMap: AutoHaulerSlot[]
  legalEnvelope?: {
    maxOverallHeightIn?: number
    maxOverallLengthIn?: number
    maxOverallWidthIn?: number
    notes?: string[]
  }
  sourceConfidence?: number
  requiresManualVerification?: boolean
}

export interface EquipmentCombo {
  id: string
  comboKey: string
  comboName: string
  tractorSpecId?: string | null
  trailerSpecId?: string | null
  rigType?: string | null
  nominalVehicleCapacity?: number | null
  geometry?: AutoHaulerGeometry | null
  fitNotes?: string[]
  sourceNotes?: string | null
  confidence: number
  reviewStatus: string
  isActive?: boolean
}

export interface EquipmentComboListResponse {
  count: number
  combos: EquipmentCombo[]
}

export interface TrailerProfileApplyComboResponse {
  jobId: string
  combo: EquipmentCombo
  equipmentProfile: EquipmentProfile
  autoHaulerGeometry: AutoHaulerGeometry | null
  warnings?: string[]
}

// ─── Vehicle Fit Intelligence Types ──────────────────────────────────────────

export interface VehicleFitProfile {
  id: string
  vin: string | null
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  bodyStyle: string | null
  vehicleClass: string | null
  sourceType: string
  confidence: number
  reviewStatus: string | null
  lengthIn: number | null
  widthIn: number | null
  heightIn: number | null
  curbWeightLb: number | null
  grossWeightLb: number | null
  wheelbaseIn: number | null
  groundClearanceIn: number | null
  isTallVehicle: boolean
  isLowClearance: boolean
  isLongWheelbase: boolean
  isHeavyVehicle: boolean
  isOversize: boolean
  warnings: string[]
}

export interface VehicleFitProfileResult {
  profile: VehicleFitProfile
  source: 'vin_cache' | 'ymm_cache' | 'manual' | 'unknown'
  confidence: number
  warnings: string[]
}

export interface VehicleFitSummaryVehicle {
  vehicleId: string
  vin: string | null
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  color: string | null
  bodyStyle: string | null
  slotId: string | null
  fitProfileStatus: 'ready' | 'fallback' | 'missing' | 'needs_review'
  fitProfileWarnings: string[]
  rawLengthIn: number | null
  rawHeightIn: number | null
  rawWeightLb: number | null
  profile: {
    profileId: string
    sourceType: string
    confidence: number | null
    reviewStatus: string | null
    vehicleClass: string | null
    lengthIn: number | null
    widthIn: number | null
    heightIn: number | null
    curbWeightLb: number | null
    grossWeightLb: number | null
    wheelbaseIn: number | null
    groundClearanceIn: number | null
    isTallVehicle: boolean
    isLowClearance: boolean
    isLongWheelbase: boolean
    isHeavyVehicle: boolean
    isOversize: boolean
    warnings: string[]
  } | null
}

export interface VehicleFitSummaryResponse {
  jobId: string
  vehicleCount: number
  resolvedCount: number
  missingCount: number
  vehicles: VehicleFitSummaryVehicle[]
  autoHaulerGeometry: AutoHaulerGeometry | null
}

export interface VehicleFitResolveJobResponse {
  jobId: string
  resolved: Array<{
    vehicleId: string; vin: string | null; make: string | null; model: string | null
    year: number | null; profileStatus: string; source: string; confidence: number
    profileId: string | null; warnings: string[]
    dimensions: { lengthIn: number | null; heightIn: number | null; curbWeightLb: number | null; wheelbaseIn: number | null; groundClearanceIn: number | null }
    riskFlags: { isTallVehicle: boolean; isLowClearance: boolean; isLongWheelbase: boolean; isHeavyVehicle: boolean; isOversize: boolean }
  }>
  unresolved: Array<{ vehicleId: string; vin: string | null; make: string | null; model: string | null; year: number | null; profileStatus: string; warnings: string[] }>
  warnings: string[]
  summary: { total: number; resolvedCount: number; unresolvedCount: number; vinCacheCount: number; ymmCacheCount: number; manualCount: number }
}



export interface TrailerLayoutResponse {
  jobId: string
  jobNumber?: string
  deck: TrailerSlotMeta[]
  assigned: Array<{ slotId: string; vehicleId: string; vin: string; make?: string; model?: string; color?: string }>
  unassigned: Array<{ vehicleId: string; vin: string; make?: string; model?: string; color?: string }>
  deckMeta: { rows: number; cols: number; slotSize: number; assignments: Array<{ slotId: string; vehicleId: string }> }
  equipmentProfile?: EquipmentProfile
  warnings?: string[]
  autoHaulerGeometry?: AutoHaulerGeometry | null
  comboMeta?: { comboKey: string; comboName?: string | null; rigType?: string | null; nominalVehicleCapacity?: number | null } | null
}

export interface AutoArrangeResponse {
  jobId: string
  assignments: Array<{ slotId: string; vehicleId: string }>
  warnings: string[]
  blockedVehicles?: Array<{ vehicleId: string; vin: string | null; reasons: string[] }>
  fitWarnings?: string[]
  missingVehicleProfiles?: Array<{ vehicleId: string; vin: string | null; make: string | null; model: string | null }>
  deck: TrailerSlotMeta[]
  equipmentProfile?: EquipmentProfile
  autoHaulerGeometry?: AutoHaulerGeometry | null
  driverNotes: { loadOrder: string; unloadOrder: string }
}

export interface TrailerProfileUpdateResponse {
  jobId: string
  equipmentProfile: EquipmentProfile
  warnings?: string[]
}

export interface EquipmentSpec {
  id: string
  category: 'truck' | 'trailer'
  manufacturer: string
  model: string
  trimName?: string | null
  specKey: string
  schemaVersion: string
  confidence: number
  reviewStatus?: 'auto_parsed' | 'needs_review' | 'manual_seed' | null
  parserKey?: string | null
  parsedAt?: string | null
  validationWarnings?: string[] | null
  sourceUrl?: string | null
  sourceDate?: string | null
  sourceNote?: string | null
  lengthIn?: number | null
  widthIn?: number | null
  heightIn?: number | null
  interiorHeightFrontIn?: number | null
  interiorHeightRearIn?: number | null
  interiorWidthIn?: number | null
  deckType?: string | null
  deckCount?: number | null
  deckLengthsIn?: number[] | null
  deckHeightLowerIn?: number | null
  deckHeightUpperIn?: number | null
  rampLengthIn?: number | null
  kingpinToFrontIn?: number | null
  axleCount?: number | null
  axleSpacingsIn?: number[] | null
  axleWeightLimitLb?: number | null
  steerAxleMaxLb?: number | null
  driveAxleMaxLb?: number | null
  trailerAxleMaxLb?: number | null
  gcwrLb?: number | null
  gvwrLb?: number | null
  emptyWeightLb?: number | null
  tareWeightLb?: number | null
  payloadLb?: number | null
  floorRatingLb?: number | null
  wheelbaseIn?: number | null
  metadata?: Record<string, unknown>
}

export interface EquipmentSpecListResponse {
  category: 'truck' | 'trailer'
  count: number
  specs: EquipmentSpec[]
}

export interface EquipmentSchemaResponse {
  version: string
  categories: string[]
  fields: Record<string, string[]>
}

export interface EquipmentRefreshResponse {
  queued: number
  docs: Array<{ id: string; url: string; source_name: string }>
  note: string
}

export interface TrailerProfileApplySpecResponse {
  jobId: string
  spec: EquipmentSpec
  equipmentProfile: EquipmentProfile
  warnings?: string[]
}

export interface CarrierPlan {
  carrierId: string
  planName: 'BASIC' | 'PRO' | 'FINANCE' | 'ENTERPRISE'
  status: 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'CANCELED'
  walletEnabled: boolean
  payoutsEnabled: boolean
  fuelAdvancesEnabled: boolean
  expensesEnabled: boolean
  settlementsEnabled: boolean
}

export interface DriverPermissions {
  canViewWallet: boolean
  canViewSettlements: boolean
  canRequestPayout: boolean
  canRequestFuelAdvance: boolean
  canSubmitExpenses: boolean
  canViewCompanyLoads: boolean
}

export interface DriverWalletView {
  carrierId: string
  driverId: string
  wallet: {
    walletId: string
    currency: string
    postedBalance: string
    availableBalance: string
    heldBalance: string
    pendingPayouts: string
  }
  summary: {
    pendingSettlements: string
    availableForPayout: string
    activeFuelAdvances: string
    lifetimeSettlements: string
    lifetimePayouts: string
  }
  permissions: DriverPermissions
  plan: Pick<CarrierPlan, 'planName' | 'walletEnabled' | 'payoutsEnabled' | 'fuelAdvancesEnabled'>
}

export interface FuelAdvance {
  id: string
  carrierId: string
  driverId: string
  jobId: string
  status: string
  requestedAmount: string
  approvedAmount?: string
  remainingAmount?: string
  currency: string
  reason?: string
  denialReason?: string
  requestedAt: string
  approvedAt?: string
  deniedAt?: string
  disbursedAt?: string
  repaidAt?: string
}

export const financeApi = {
  getCarrierPlan: (carrierId: string) =>
    jobClient.get<{ plan: CarrierPlan }>(`/carriers/${carrierId}/plan`),

  upsertCarrierPlan: (carrierId: string, payload: Omit<CarrierPlan, 'carrierId'>) =>
    jobClient.put<{ plan: CarrierPlan }>(`/carriers/${carrierId}/plan`, payload),

  listCarrierDrivers: (carrierId: string) =>
    jobClient.get<{ drivers: Array<{ id: string; carrierId: string; driverId: string; status: string; role: string }> }>(`/carriers/${carrierId}/drivers`),

  addCarrierDriver: (carrierId: string, payload: { driverId: string; status: string; role: string }) =>
    jobClient.post<{ carrierDriver: { id: string; carrierId: string; driverId: string; status: string; role: string } }>(`/carriers/${carrierId}/drivers`, payload),

  getDriverPermissions: (carrierId: string, driverId: string) =>
    jobClient.get<{ permissions: DriverPermissions }>(`/carriers/${carrierId}/drivers/${driverId}/permissions`),

  updateDriverPermissions: (carrierId: string, driverId: string, payload: Partial<DriverPermissions>) =>
    jobClient.patch<{ permissions: DriverPermissions }>(`/carriers/${carrierId}/drivers/${driverId}/permissions`, payload),

  getDriverWallet: (carrierId: string, driverId: string) =>
    jobClient.get<DriverWalletView>(`/carriers/${carrierId}/drivers/${driverId}/wallet`),

  listDriverSettlements: (carrierId: string, driverId: string) =>
    jobClient.get<{ settlements: any[] }>(`/carriers/${carrierId}/drivers/${driverId}/settlements`),

  listDriverPayouts: (carrierId: string, driverId: string) =>
    jobClient.get<{ payouts: any[] }>(`/carriers/${carrierId}/drivers/${driverId}/payouts`),

  requestDriverPayout: (carrierId: string, driverId: string, payload: { amount: string; method?: 'INTERNAL' | 'ACH_FUTURE' | 'INSTANT_FUTURE' }) =>
    jobClient.post<{ payout: any }>(`/carriers/${carrierId}/drivers/${driverId}/payouts`, payload),

  listDriverFuelAdvances: (carrierId: string, driverId: string) =>
    jobClient.get<{ fuelAdvances: FuelAdvance[] }>(`/carriers/${carrierId}/drivers/${driverId}/fuel-advances`),

  requestFuelAdvance: (carrierId: string, driverId: string, payload: { jobId: string; requestedAmount: string; reason?: string }) =>
    jobClient.post<FuelAdvance>(`/carriers/${carrierId}/drivers/${driverId}/fuel-advances`, payload),

  approveFuelAdvance: (id: string, payload: { approvedAmount?: string; approvalNote?: string }) =>
    jobClient.post<FuelAdvance>(`/fuel-advances/${id}/approve`, payload),

  denyFuelAdvance: (id: string, payload: { reason: string }) =>
    jobClient.post<FuelAdvance>(`/fuel-advances/${id}/deny`, payload),

  disburseFuelAdvance: (id: string) =>
    jobClient.post<FuelAdvance>(`/fuel-advances/${id}/disburse`, {}),
}

// ─── Driver Profile Settings ──────────────────────────────────────────────────

export interface DriverProfile {
  id?: string
  auxUserId: string
  carrierOrgId?: string | null
  cdlClass?: string | null
  licenseNumber?: string | null
  licenseState?: string | null
  licenseExpires?: string | null
  medicalCertExpires?: string | null
  endorsements: string[]
  restrictions: string[]
  yearsExperience?: number | null
  phone?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  unitLabel?: string | null
  notes?: string | null
  isActive?: boolean
  complianceWarnings?: string[]
  createdAt?: string
  updatedAt?: string
}

export interface DriverEquipmentAssignment {
  id: string
  auxUserId: string
  carrierOrgId: string
  comboKey?: string | null
  tractorSpecId?: string | null
  trailerSpecId?: string | null
  tractorManufacturer?: string | null
  tractorModel?: string | null
  trailerManufacturer?: string | null
  trailerModel?: string | null
  unitLabel?: string | null
  assignedBy?: string | null
  assignedAt?: string
}

export interface DriverProfileEntry {
  auxUserId: string
  email?: string
  name?: string
  roles?: string[]
  profile: DriverProfile | null
  equipment: DriverEquipmentAssignment | null
}

export interface DriverProfileListResponse {
  carrierId: string
  drivers: DriverProfileEntry[]
}

export const settingsApi = {
  /** Get calling user's own driver profile */
  getProfile: () =>
    jobClient.get<{ profile: DriverProfile }>('/settings/profile'),

  /** Driver self-service: update phone + emergency contact */
  updateProfile: (data: { phone?: string; emergencyContactName?: string; emergencyContactPhone?: string }) =>
    jobClient.patch<{ profile: DriverProfile }>('/settings/profile', data),

  /** Get calling user's active equipment assignment */
  getEquipment: () =>
    jobClient.get<{ assignment: DriverEquipmentAssignment | null }>('/settings/equipment'),

  /** List all drivers with TMS profiles for a carrier */
  listDriverProfiles: (carrierId: string) =>
    jobClient.get<DriverProfileListResponse>(`/carriers/${carrierId}/driver-profiles`),

  /** Upsert a driver profile (carrier admin) */
  upsertDriverProfile: (carrierId: string, data: Partial<DriverProfile> & { auxUserId: string }) =>
    jobClient.post<{ profile: DriverProfile }>(`/carriers/${carrierId}/driver-profiles`, data),

  /** Get a single driver profile */
  getDriverProfile: (carrierId: string, driverId: string) =>
    jobClient.get<DriverProfileEntry>(`/carriers/${carrierId}/driver-profiles/${driverId}`),

  /** Update a driver profile (carrier admin) */
  updateDriverProfile: (carrierId: string, driverId: string, data: Partial<DriverProfile>) =>
    jobClient.patch<{ profile: DriverProfile }>(`/carriers/${carrierId}/driver-profiles/${driverId}`, data),

  /** Assign an equipment combo to a driver */
  assignEquipment: (carrierId: string, driverId: string, data: {
    comboKey?: string
    tractorSpecId?: string
    trailerSpecId?: string
    unitLabel?: string
  }) =>
    jobClient.put<{ assignment: DriverEquipmentAssignment }>(
      `/carriers/${carrierId}/driver-profiles/${driverId}/equipment`,
      data
    ),

  /** Remove a driver's equipment assignment */
  removeEquipment: (carrierId: string, driverId: string) =>
    jobClient.delete<{ message: string }>(`/carriers/${carrierId}/driver-profiles/${driverId}/equipment`),
}

// ─── Carrier Equipment Units ───────────────────────────────────────────────────

export interface CarrierEquipmentUnit {
  id: string
  carrierOrgId?: string
  unitType: 'tractor' | 'trailer' | 'combo'
  unitNumber?: string | null
  displayName?: string | null
  equipmentSpecId?: string | null
  equipmentComboId?: string | null
  vin?: string | null
  year?: number | null
  make?: string | null
  model?: string | null
  trim?: string | null
  plateNumber?: string | null
  plateState?: string | null
  ownershipType?: string | null
  unitStatus: 'ACTIVE' | 'INACTIVE' | 'IN_SHOP' | 'RETIRED'
  registrationExpires?: string | null
  inspectionExpires?: string | null
  insuranceExpires?: string | null
  notes?: string | null
  specManufacturer?: string | null
  specModel?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface FleetFinanceAccount {
  id: string
  carrierOrgId?: string | null
  equipmentUnitId: string
  financeType?: string | null
  ownershipType?: string | null
  lenderName?: string | null
  lessorName?: string | null
  lienholderName?: string | null
  accountNumberLast4?: string | null
  contractNumber?: string | null
  originalAmountCents?: number | null
  downPaymentCents?: number | null
  currentBalanceCents?: number | null
  payoffAmountCents?: number | null
  payoffGoodThrough?: string | null
  aprPercent?: number | null
  interestType?: string | null
  termMonths?: number | null
  termWeeks?: number | null
  startDate?: string | null
  maturityDate?: string | null
  paymentAmountCents?: number | null
  paymentFrequency?: string | null
  paymentDayOfMonth?: number | null
  nextDueDate?: string | null
  lastPaidDate?: string | null
  balloonPaymentCents?: number | null
  residualValueCents?: number | null
  purchaseOptionCents?: number | null
  settlementDeductionEnabled?: boolean
  settlementDeductionAmountCents?: number | null
  settlementDeductionFrequency?: string | null
  status?: string | null
  notes?: string | null
  metadata?: Record<string, unknown>
  warnings?: string[]
  unit?: {
    id: string
    unitType?: string | null
    unitNumber?: string | null
    displayName?: string | null
    insuranceExpires?: string | null
    registrationExpires?: string | null
    inspectionExpires?: string | null
  }
  createdAt?: string
  updatedAt?: string
}

export interface FleetFinancePayment {
  id: string
  financeAccountId: string
  paymentDate: string
  dueDate?: string | null
  amountCents: number
  principalCents?: number | null
  interestCents?: number | null
  feesCents?: number | null
  paymentMethod?: string | null
  referenceNumber?: string | null
  externalPaymentId?: string | null
  status?: string | null
  notes?: string | null
  createdByUserId?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface FleetFinanceOverview {
  monthlyPaymentTotalCents: number
  weeklyPaymentTotalCents: number
  activeLoanCount: number
  activeLeaseCount: number
  pastDueCount: number
  dueSoonCount: number
  totalCurrentBalanceCents: number
  warnings: string[]
}

export interface FullDriverProfile {
  id?: string
  auxUserId: string
  carrierOrgId?: string | null
  firstName?: string | null
  middleName?: string | null
  lastName?: string | null
  legalName?: string | null
  dateOfBirth?: string | null
  email?: string | null
  phone?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  cdlClass?: string | null
  licenseNumber?: string | null
  licenseState?: string | null
  licenseExpires?: string | null
  medicalCertExpires?: string | null
  endorsements?: string[]
  restrictions?: string[]
  yearsExperience?: number | null
  mvrLastChecked?: string | null
  mvrStatus?: string | null
  backgroundCheckStatus?: string | null
  drugTestStatus?: string | null
  drugTestDate?: string | null
  hireDate?: string | null
  driverStatus?: string
  unitLabel?: string | null
  notes?: string | null
  isActive?: boolean
  defaultTractorUnitId?: string | null
  defaultTrailerUnitId?: string | null
  defaultComboId?: string | null
  maxVehicleCapacity?: number | null
  operatingStates?: string[]
  preferredLanes?: string[]
  complianceWarnings?: string[]
  createdAt?: string
  updatedAt?: string
}

export interface DriverRosterEntry {
  auxUserId: string
  email?: string | null
  name?: string | null
  profile: FullDriverProfile | null
  tractorUnit?: CarrierEquipmentUnit | null
  trailerUnit?: CarrierEquipmentUnit | null
}

export const carrierApi = {
  // ── Equipment Units ─────────────────────────────────────────────
  listEquipment: (params?: { type?: string; status?: string }) =>
    jobClient.get<{ units: CarrierEquipmentUnit[] }>('/carrier/equipment', { params }),

  createEquipment: (data: Partial<CarrierEquipmentUnit> & { unitType: string }) =>
    jobClient.post<{ unit: CarrierEquipmentUnit }>('/carrier/equipment', data),

  updateEquipment: (unitId: string, data: Partial<CarrierEquipmentUnit>) =>
    jobClient.patch<{ unit: CarrierEquipmentUnit }>(`/carrier/equipment/${unitId}`, data),

  retireEquipment: (unitId: string) =>
    jobClient.delete<{ message: string }>(`/carrier/equipment/${unitId}`),

  // ── Fleet Finance ───────────────────────────────────────────────
  listFinanceAccounts: () =>
    jobClient.get<{ accounts: FleetFinanceAccount[] }>('/carrier/fleet/finance'),

  getFinanceOverview: () =>
    jobClient.get<FleetFinanceOverview>('/carrier/fleet/finance/overview'),

  getUnitFinance: (unitId: string) =>
    jobClient.get<{ unit: CarrierEquipmentUnit; accounts: FleetFinanceAccount[] }>(`/carrier/equipment/${unitId}/finance`),

  createUnitFinance: (unitId: string, data: Partial<FleetFinanceAccount>) =>
    jobClient.post<{ account: FleetFinanceAccount }>(`/carrier/equipment/${unitId}/finance`, data),

  updateUnitFinance: (unitId: string, financeAccountId: string, data: Partial<FleetFinanceAccount>) =>
    jobClient.patch<{ account: FleetFinanceAccount }>(`/carrier/equipment/${unitId}/finance/${financeAccountId}`, data),

  deleteUnitFinance: (unitId: string, financeAccountId: string) =>
    jobClient.delete<{ message: string }>(`/carrier/equipment/${unitId}/finance/${financeAccountId}`),

  listFinancePayments: (unitId: string, financeAccountId: string) =>
    jobClient.get<{ payments: FleetFinancePayment[] }>(`/carrier/equipment/${unitId}/finance/${financeAccountId}/payments`),

  createFinancePayment: (unitId: string, financeAccountId: string, data: Partial<FleetFinancePayment>) =>
    jobClient.post<{ payment: FleetFinancePayment }>(`/carrier/equipment/${unitId}/finance/${financeAccountId}/payments`, data),

  updateFinancePayment: (unitId: string, financeAccountId: string, paymentId: string, data: Partial<FleetFinancePayment>) =>
    jobClient.patch<{ payment: FleetFinancePayment }>(`/carrier/equipment/${unitId}/finance/${financeAccountId}/payments/${paymentId}`, data),

  // ── Drivers ─────────────────────────────────────────────────────
  listDrivers: () =>
    jobClient.get<{ orgId: string; drivers: DriverRosterEntry[] }>('/carrier/drivers'),

  createDriver: (data: Partial<FullDriverProfile> & { auxUserId: string }) =>
    jobClient.post<{ profile: FullDriverProfile }>('/carrier/drivers', data),

  getDriver: (driverId: string) =>
    jobClient.get<{ profile: FullDriverProfile; tractorUnit: CarrierEquipmentUnit | null; trailerUnit: CarrierEquipmentUnit | null; email?: string; name?: string }>(`/carrier/drivers/${driverId}`),

  updateDriver: (driverId: string, data: Partial<FullDriverProfile>) =>
    jobClient.patch<{ profile: FullDriverProfile }>(`/carrier/drivers/${driverId}`, data),

  assignEquipment: (driverId: string, data: {
    tractorUnitId?: string
    trailerUnitId?: string
    comboId?: string
    notes?: string
  }) =>
    jobClient.post<{ driverProfile: FullDriverProfile; tractorUnit: CarrierEquipmentUnit | null; trailerUnit: CarrierEquipmentUnit | null; warnings: string[] }>(`/carrier/drivers/${driverId}/equipment-assignment`, data),

  getSensitive: (driverId: string) =>
    jobClient.get<{ sensitiveData: { ssnLast4: string | null; taxIdLast4: string | null; accessPolicy: string } | null; ssnOnFile: boolean; taxIdOnFile: boolean }>(`/carrier/drivers/${driverId}/sensitive`),

  updateSensitive: (driverId: string, data: { ssnLast4?: string; taxIdLast4?: string }) =>
    jobClient.put<{ message: string; ssnOnFile: boolean; taxIdOnFile: boolean }>(`/carrier/drivers/${driverId}/sensitive`, data),
}

export const driverApi = {
  getMe: () =>
    jobClient.get<{ profile: FullDriverProfile; tractorUnit: CarrierEquipmentUnit | null; trailerUnit: CarrierEquipmentUnit | null }>('/driver/settings/me'),

  updateMe: (data: Partial<FullDriverProfile>) =>
    jobClient.patch<{ profile: FullDriverProfile }>('/driver/settings/me', data),

  requestEquipment: (data: { tractorDescription?: string; trailerDescription?: string; notes?: string }) =>
    jobClient.post<{ message: string }>('/driver/settings/me/equipment-request', data),
}

export default api
